/**
 * @file useInputEx.js
 * @description フォーム入力フィールドを簡単に生成するためのカスタムフック
 *
 * このフックは、Material-UIのコンポーネントを使用して、
 * スタイリッシュで機能的なフォーム入力フィールドを提供します。
 * 特に、パスワード入力フィールドの可視性切り替え機能を
 * 簡単に実装できるように設計されています。
 *
 * @example
 * // 基本的な使用例
 * const [form, formProps] = useFormEx();
 * const { input_password } = useInputEx(form, formProps);
 *
 * // コンポーネント内での使用
 * return (
 *   <form>
 *     {input_password({
 *       name: "userPassword",
 *       label: "パスワードを入力",
 *       required: true
 *     })}
 *   </form>
 * );
 *
 * @author MetroJS Team
 * @version 1.0.0
 * @since 2024-03-01
 */

"use client";

import logjs from "@metrojs/logjs";

const log = new logjs("useInputEx");

const baseStyle = {
  padding: "3px 5px",
  border: "1px solid #ccc",
  borderRadius: "3px",
  width: "160px",
}; // ベーススタイル

/**
 * フォーム入力フィールドを生成するためのカスタムフック
 * @param {Object} form - フォームデータオブジェクト
 * @param {Object} formProps - フォームプロパティオブジェクト
 * @returns {Object} 各種入力フィールド生成関数のオブジェクト
 */
const useInputEx = (form = {}, formProps = {}) => {
  const is_standard = true; // 標準のinput要素を使用するかどうか
  /**
   * ID入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} ID入力フィールドのJSX要素
   */
  const input_id = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_id form.id:${form?.id || ""} params:`, params);
    return formProps.input("id", { label: "ID", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 企業ID入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 企業ID入力フィールドのJSX要素
   */
  const input_CorpID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CorpID form.CorpID:${form?.CorpID || ""} params:`, params);
    return formProps.input("CorpID", { label: "企業ID", maxlength: 12, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 請求書番号　重複なし入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 請求書番号　重複なし入力フィールドのJSX要素
   */
  const input_Seikyu_No = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Seikyu_No form.Seikyu_No:${form?.Seikyu_No || ""} params:`, params);
    return formProps.input("Seikyu_No", { label: "請求書番号　重複なし", maxlength: 12, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 締め処理フラグ　1=部品、3=車輌入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 締め処理フラグ　1=部品、3=車輌入力フィールドのJSX要素
   */
  const input_Wellset_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Wellset_flg form.Wellset_flg:${form?.Wellset_flg || ""} params:`, params);
    return formProps.input("Wellset_flg", { label: "締め処理フラグ　1=部品、3=車輌", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 請求金額(税込)入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 請求金額(税込)入力フィールドのJSX要素
   */
  const input_Seikyu_Gaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Seikyu_Gaku form.Seikyu_Gaku:${form?.Seikyu_Gaku || ""} params:`, params);
    return formProps.input("Seikyu_Gaku", { label: "請求金額(税込)", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 今回請求額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 今回請求額入力フィールドのJSX要素
   */
  const input_Gokei_Gaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Gokei_Gaku form.Gokei_Gaku:${form?.Gokei_Gaku || ""} params:`, params);
    return formProps.input("Gokei_Gaku", { label: "今回請求額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 支払充当金額(税込)　入金伝票入力から適用消込入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 支払充当金額(税込)　入金伝票入力から適用消込入力フィールドのJSX要素
   */
  const input_Jyuto_Gaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Jyuto_Gaku form.Jyuto_Gaku:${form?.Jyuto_Gaku || ""} params:`, params);
    return formProps.input("Jyuto_Gaku", { label: "支払充当金額(税込)　入金伝票入力から適用消込", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 残額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 残額入力フィールドのJSX要素
   */
  const input_Zan_Gaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Zan_Gaku form.Zan_Gaku:${form?.Zan_Gaku || ""} params:`, params);
    return formProps.input("Zan_Gaku", { label: "残額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * Movi手数料入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} Movi手数料入力フィールドのJSX要素
   */
  const input_Movi_Gaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Movi_Gaku form.Movi_Gaku:${form?.Movi_Gaku || ""} params:`, params);
    return formProps.input("Movi_Gaku", { label: "Movi手数料", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 税額通常用入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 税額通常用入力フィールドのJSX要素
   */
  const input_zei_gaku1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_zei_gaku1 form.zei_gaku1:${form?.zei_gaku1 || ""} params:`, params);
    return formProps.input("zei_gaku1", { label: "税額通常用", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 税額複数用入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 税額複数用入力フィールドのJSX要素
   */
  const input_zei_gaku2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_zei_gaku2 form.zei_gaku2:${form?.zei_gaku2 || ""} params:`, params);
    return formProps.input("zei_gaku2", { label: "税額複数用", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * ダウンロードフラグ 1=済入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} ダウンロードフラグ 1=済入力フィールドのJSX要素
   */
  const input_dl_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_dl_flg form.dl_flg:${form?.dl_flg || ""} params:`, params);
    return formProps.input("dl_flg", { label: "ダウンロードフラグ 1=済", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 最終閲覧者ID入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 最終閲覧者ID入力フィールドのJSX要素
   */
  const input_dl_userid = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_dl_userid form.dl_userid:${form?.dl_userid || ""} params:`, params);
    return formProps.input("dl_userid", { label: "最終閲覧者ID", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 備考入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 備考入力フィールドのJSX要素
   */
  const input_Bikou = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Bikou form.bikou:${form?.bikou || ""} params:`, params);
    return formProps.input("Bikou", { label: "備考", multiline: true, maxRows: 10, rows: 4, maxlength: 200, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };

  /**
   * ステータス 0:新規 5:有効 99:無効入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} ステータス 0:新規 5:有効 99:無効入力フィールドのJSX要素
   */
  const input_status = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_status form.status:${form?.status || ""} params:`, params);
    return formProps.input("status", { label: "ステータス 0:新規 5:有効 99:無効", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: { ...baseStyle, width: "98%" }, ...params }, is_debug);
  };
  /**
   * 情報登録者入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 情報登録者入力フィールドのJSX要素
   */
  const input_registid = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_registid form.registid:${form?.registid || ""} params:`, params);
    return formProps.input("registid", { label: "情報登録者", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 情報更新者入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 情報更新者入力フィールドのJSX要素
   */
  const input_henkokind = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_henkokind form.henkokind:${form?.henkokind || ""} params:`, params);
    return formProps.input("henkokind", { label: "情報更新者", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 情報削除者入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 情報削除者入力フィールドのJSX要素
   */
  const input_deleteid = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_deleteid form.deleteid:${form?.deleteid || ""} params:`, params);
    return formProps.input("deleteid", { label: "情報削除者", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 見積番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 見積番号入力フィールドのJSX要素
   */
  const input_MitsumoriNo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_MitsumoriNo form.MitsumoriNo:${form?.MitsumoriNo || ""} params:`, params);
    return formProps.input("MitsumoriNo", { label: "見積番号", isNumAndAlpha: true, maxlength: 12, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 出品者入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 出品者入力フィールドのJSX要素
   */
  const input_SellCorpID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_SellCorpID form.SellCorpID:${form?.SellCorpID || ""} params:`, params);
    return formProps.input("SellCorpID", { label: "出品者", maxlength: 12, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 落札者入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 落札者入力フィールドのJSX要素
   */
  const input_BuyCorpID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_BuyCorpID form.BuyCorpID:${form?.BuyCorpID || ""} params:`, params);
    return formProps.input("BuyCorpID", { label: "落札者", maxlength: 12, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 書類有無　1:有　9:無入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 書類有無　1:有　9:無入力フィールドのJSX要素
   */
  const input_Shorui_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shorui_flg form.Shorui_flg:${form?.Shorui_flg || ""} params:`, params);
    return formProps.input("Shorui_flg", { label: "書類有無　1:有　9:無", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 金額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 金額入力フィールドのJSX要素
   */
  const input_Kingaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Kingaku form.Kingaku:${form?.Kingaku || ""} params:`, params);
    return formProps.input("Kingaku", { label: "金額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌税額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌税額入力フィールドのJSX要素
   */
  const input_Sharyo_Zei_Gaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Zei_Gaku form.Sharyo_Zei_Gaku:${form?.Sharyo_Zei_Gaku || ""} params:`, params);
    return formProps.input("Sharyo_Zei_Gaku", { label: "車輌税額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * Movi手数料税額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} Movi手数料税額入力フィールドのJSX要素
   */
  const input_Movi_Zei_Gaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Movi_Zei_Gaku form.Movi_Zei_Gaku:${form?.Movi_Zei_Gaku || ""} params:`, params);
    return formProps.input("Movi_Zei_Gaku", { label: "Movi手数料税額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 書類送料額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 書類送料額入力フィールドのJSX要素
   */
  const input_Shorui_Gaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shorui_Gaku form.Shorui_Gaku:${form?.Shorui_Gaku || ""} params:`, params);
    return formProps.input("Shorui_Gaku", { label: "書類送料額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 書類送料税額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 書類送料税額入力フィールドのJSX要素
   */
  const input_Shorui_Zei_Gaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shorui_Zei_Gaku form.Shorui_Zei_Gaku:${form?.Shorui_Zei_Gaku || ""} params:`, params);
    return formProps.input("Shorui_Zei_Gaku", { label: "書類送料税額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 書類送料 1:あり 0:なし入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 書類送料 1:あり 0:なし入力フィールドのJSX要素
   */
  const input_ShoruiSoryo_Flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ShoruiSoryo_Flg form.ShoruiSoryo_Flg:${form?.ShoruiSoryo_Flg || ""} params:`, params);
    return formProps.input("ShoruiSoryo_Flg", { label: "書類送料 1:あり 0:なし", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 書類送付書フラグ 1:あり 9:なし入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 書類送付書フラグ 1:あり 9:なし入力フィールドのJSX要素
   */
  const input_ShoruiSoufu_Flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ShoruiSoufu_Flg form.ShoruiSoufu_Flg:${form?.ShoruiSoufu_Flg || ""} params:`, params);
    return formProps.input("ShoruiSoufu_Flg", { label: "書類送付書フラグ 1:あり 9:なし", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 受取書類チェック入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 受取書類チェック入力フィールドのJSX要素
   */
  const input_ShoruiUketori_Chk = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ShoruiUketori_Chk form.ShoruiUketori_Chk:${form?.ShoruiUketori_Chk || ""} params:`, params);
    return formProps.input("ShoruiUketori_Chk", { label: "受取書類チェック", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 書類受取その他入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 書類受取その他入力フィールドのJSX要素
   */
  const input_ShoruiUketori_Text = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ShoruiUketori_Text form.ShoruiUketori_Text:${form?.ShoruiUketori_Text || ""} params:`, params);
    return formProps.input("ShoruiUketori_Text", { label: "書類受取その他", maxlength: 200, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * サイト名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} サイト名入力フィールドのJSX要素
   */
  const input_SiteName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_SiteName form.SiteName:${form?.SiteName || ""} params:`, params);
    return formProps.input("SiteName", { label: "サイト名", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * トラック市:同志番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} トラック市:同志番号入力フィールドのJSX要素
   */
  const input_Seqnum = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Seqnum form.Seqnum:${form?.Seqnum || ""} params:`, params);
    return formProps.input("Seqnum", { label: "トラック市:同志番号", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 企業名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 企業名入力フィールドのJSX要素
   */
  const input_CorpName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CorpName form.CorpName:${form?.CorpName || ""} params:`, params);
    return formProps.input("CorpName", { label: "企業名", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 整備支店名入力フィールドを生成する関数（部品見積一覧検索用）
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 整備支店名入力フィールドのJSX要素
   */
  const input_Shiten = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shiten form.Shiten:${form?.Shiten || ""} params:`, params);
    return formProps.input("Shiten", { label: "整備支店名", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 担当者名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 担当者名入力フィールドのJSX要素
   */
  const input_Tantosha = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Tantosha form.Tantosha:${form?.Tantosha || ""} params:`, params);
    return formProps.input("Tantosha", { label: "担当者名", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * メールアドレス入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} メールアドレス入力フィールドのJSX要素
   */
  const input_Email = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Email form.Email:${form?.Email || ""} params:`, params);
    return formProps.input("Email", { label: "メールアドレス", maxlength: 100, isMailAddress: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 電話番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 電話番号入力フィールドのJSX要素
   */
  const input_Tel = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Tel form.Tel:${form?.Tel || ""} params:`, params);
    return formProps.input("Tel", { label: "電話番号", isNumberOnly: true, maxlength: 30, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * FAX番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} FAX番号入力フィールドのJSX要素
   */
  const input_Fax = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Fax form.Fax:${form?.Fax || ""} params:`, params);
    return formProps.input("Fax", { label: "FAX番号", isNumberOnly: true, maxlength: 30, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * お問い合わせ種類　1:生産者 2:整備入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} お問い合わせ種類　1:生産者 2:整備入力フィールドのJSX要素
   */
  const input_ContactKind = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ContactKind form.ContactKind:${form?.ContactKind || ""} params:`, params);
    return formProps.input("ContactKind", { label: "お問い合わせ種類　1:生産者 2:整備", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * お問い合わせ内容入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} お問い合わせ内容入力フィールドのJSX要素
   */
  const input_ContactText = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ContactText form.ContactText:${form?.ContactText || ""} params:`, params);
    return formProps.input("ContactText", { label: "お問い合わせ内容", maxlength: 1000, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * リファラ入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} リファラ入力フィールドのJSX要素
   */
  const input_Referer = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Referer form.Referer:${form?.Referer || ""} params:`, params);
    return formProps.input("Referer", { label: "リファラ", maxlength: 500, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * アクセス元ホスト名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} アクセス元ホスト名入力フィールドのJSX要素
   */
  const input_UserHostName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_UserHostName form.UserHostName:${form?.UserHostName || ""} params:`, params);
    return formProps.input("UserHostName", { label: "アクセス元ホスト名", maxlength: 500, isIpAddress: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * グループ・団体ID（DHM_Groups.ID）入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} グループ・団体ID（DHM_Groups.ID）入力フィールドのJSX要素
   */
  const input_Group_ID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Group_ID form.Group_ID:${form?.Group_ID || ""} params:`, params);
    return formProps.input("Group_ID", { label: "グループ・団体ID（DHM_Groups.ID）", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 伝票番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 伝票番号入力フィールドのJSX要素
   */
  const input_DenpyouNo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_DenpyouNo form.DenpyouNo:${form?.DenpyouNo || ""} params:`, params);
    return formProps.input("DenpyouNo", { label: "伝票番号", maxlength: 11, is_standard, autoComplete: "on", style: { ...baseStyle, size: "35", maxLength: "60" }, ...params }, is_debug);
  };
  /**
   * 購入したPAID入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 購入したPAID入力フィールドのJSX要素
   */
  const input_PAIDs = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_PAIDs form.PAIDs:${form?.PAIDs || ""} params:`, params);
    return formProps.input("PAIDs", { label: "購入したPAID", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * DHD_SalesPaymentsのid入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} DHD_SalesPaymentsのid入力フィールドのJSX要素
   */
  const input_DSPID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_DSPID form.DSPID:${form?.DSPID || ""} params:`, params);
    return formProps.input("DSPID", { label: "DHD_SalesPaymentsのid", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 種別 1:部品 2:車輌入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 種別 1:部品 2:車輌入力フィールドのJSX要素
   */
  const input_BuhSha_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_BuhSha_flg form.BuhSha_flg:${form?.BuhSha_flg || ""} params:`, params);
    return formProps.input("BuhSha_flg", { label: "種別 1:部品 2:車輌", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 請求ID入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 請求ID入力フィールドのJSX要素
   */
  const input_WellsetID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_WellsetID form.WellsetID:${form?.WellsetID || ""} params:`, params);
    return formProps.input("WellsetID", { label: "請求ID", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * グループID入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} グループID入力フィールドのJSX要素
   */
  const input_GroupID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_GroupID form.GroupID:${form?.GroupID || ""} params:`, params);
    return formProps.input("GroupID", { label: "グループID", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 支払金額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 支払金額入力フィールドのJSX要素
   */
  const input_Expense_amount = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Expense_amount form.Expense_amount:${form?.Expense_amount || ""} params:`, params);
    return formProps.input("Expense_amount", { label: "支払金額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 入金金額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 入金金額入力フィールドのJSX要素
   */
  const input_Payment_amount = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Payment_amount form.Payment_amount:${form?.Payment_amount || ""} params:`, params);
    return formProps.input("Payment_amount", { label: "入金金額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 支店名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 支店名入力フィールドのJSX要素
   */
  const input_SName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_SName form.SName:${form?.SName || ""} params:`, params);
    return formProps.input("SName", { label: "支店名", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 請求/支払先 1:親入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 請求/支払先 1:親入力フィールドのJSX要素
   */
  const input_Oya = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Oya form.Oya:${form?.Oya || ""} params:`, params);
    return formProps.input("Oya", { label: "請求/支払先 1:親", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 種別 1:整備 2:生産者入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 種別 1:整備 2:生産者入力フィールドのJSX要素
   */
  const input_Shubetsu = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shubetsu form.Shubetsu:${form?.Shubetsu || ""} params:`, params);
    return formProps.input("Shubetsu", { label: "種別 1:整備 2:生産者", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 整備メールフラグ 1:送信予約 5:送信完了 9:無効入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 整備メールフラグ 1:送信予約 5:送信完了 9:無効入力フィールドのJSX要素
   */
  const input_abMailFlag = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_abMailFlag form.abMailFlag:${form?.abMailFlag || ""} params:`, params);
    return formProps.input(
      "abMailFlag",
      { label: "整備メールフラグ 1:送信予約 5:送信完了 9:無効", isNumberOnly: true, isMinus: true, isMailAddress: true, is_standard, autoComplete: "on", style: baseStyle, ...params },
      is_debug
    );
  };
  /**
   * 生産者メールフラグ 1:送信予約 5:送信完了 9:無効入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 生産者メールフラグ 1:送信予約 5:送信完了 9:無効入力フィールドのJSX要素
   */
  const input_yzMailFlag = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_yzMailFlag form.yzMailFlag:${form?.yzMailFlag || ""} params:`, params);
    return formProps.input(
      "yzMailFlag",
      { label: "生産者メールフラグ 1:送信予約 5:送信完了 9:無効", isNumberOnly: true, isMinus: true, isMailAddress: true, is_standard, autoComplete: "on", style: baseStyle, ...params },
      is_debug
    );
  };
  /**
   * 支払伝票番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 支払伝票番号入力フィールドのJSX要素
   */
  const input_Expense_No = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Expense_No form.Expense_No:${form?.Expense_No || ""} params:`, params);
    return formProps.input("Expense_No", { label: "支払伝票番号", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 支払種別マスタを持つべきか現在は固定 1=現金 2=振込 3=手形 4=相殺(ここは自動計算？) 5=値引(ここは受発注だけ？) 6=その他入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 支払種別マスタを持つべきか現在は固定 1=現金 2=振込 3=手形 4=相殺(ここは自動計算？) 5=値引(ここは受発注だけ？) 6=その他入力フィールドのJSX要素
   */
  const input_Expense_kind = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Expense_kind form.Expense_kind:${form?.Expense_kind || ""} params:`, params);
    return formProps.input(
      "Expense_kind",
      {
        label: "支払種別マスタを持つべきか現在は固定 1=現金 2=振込 3=手形 4=相殺(ここは自動計算？) 5=値引(ここは受発注だけ？) 6=その他",
        isNumberOnly: true,
        isMinus: true,
        is_standard, autoComplete: "on",
        style: baseStyle,
        ...params,
      },
      is_debug
    );
  };
  /**
   * 金融機関 PMM_Financesの各企業Seqnum入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 金融機関 PMM_Financesの各企業Seqnum入力フィールドのJSX要素
   */
  const input_Expense_Finance = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Expense_Finance form.Expense_Finance:${form?.Expense_Finance || ""} params:`, params);
    return formProps.input("Expense_Finance", { label: "金融機関 PMM_Financesの各企業Seqnum", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 金融機関名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 金融機関名入力フィールドのJSX要素
   */
  const input_Expense_FIName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Expense_FIName form.Expense_FIName:${form?.Expense_FIName || ""} params:`, params);
    return formProps.input("Expense_FIName", { label: "金融機関名", maxlength: 80, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 金融機関支店名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 金融機関支店名入力フィールドのJSX要素
   */
  const input_Expense_FIShiten = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Expense_FIShiten form.Expense_FIShiten:${form?.Expense_FIShiten || ""} params:`, params);
    return formProps.input("Expense_FIShiten", { label: "金融機関支店名", maxlength: 80, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 口座種別入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 口座種別入力フィールドのJSX要素
   */
  const input_Expense_KouzaKind = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Expense_KouzaKind form.Expense_KouzaKind:${form?.Expense_KouzaKind || ""} params:`, params);
    return formProps.input("Expense_KouzaKind", { label: "口座種別", maxlength: 6, is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 口座番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 口座番号入力フィールドのJSX要素
   */
  const input_Expense_KouzaNo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Expense_KouzaNo form.Expense_KouzaNo:${form?.Expense_KouzaNo || ""} params:`, params);
    return formProps.input("Expense_KouzaNo", { label: "口座番号", maxlength: 20, is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 手形種類/番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 手形種類/番号入力フィールドのJSX要素
   */
  const input_Expense_Tegata = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Expense_Tegata form.Expense_Tegata:${form?.Expense_Tegata || ""} params:`, params);
    return formProps.input("Expense_Tegata", { label: "手形種類/番号", maxlength: 50, is_standard, style: baseStyle, ...params }, is_debug);
  };

  /**
   * 入金伝票番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 入金伝票番号入力フィールドのJSX要素
   */
  const input_Pay_No = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Pay_No form.Pay_No:${form?.Pay_No || ""} params:`, params);
    return formProps.input("Pay_No", { label: "入金伝票番号", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 入金種別マスタを持つべきか現在は固定 1=現金 2=振込 3=手形 4=相殺(ここは自動計算？) 5=値引(ここは受発注だけ？) 6=その他入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 入金種別マスタを持つべきか現在は固定 1=現金 2=振込 3=手形 4=相殺(ここは自動計算？) 5=値引(ここは受発注だけ？) 6=その他入力フィールドのJSX要素
   */
  const input_Pay_kind = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Pay_kind form.Pay_kind:${form?.Pay_kind || ""} params:`, params);
    return formProps.input(
      "Pay_kind",
      {
        label: "入金種別マスタを持つべきか現在は固定 1=現金 2=振込 3=手形 4=相殺(ここは自動計算？) 5=値引(ここは受発注だけ？) 6=その他",
        isNumberOnly: true,
        isMinus: true,
        is_standard, autoComplete: "on",
        style: baseStyle,
        ...params,
      },
      is_debug
    );
  };
  /**
   * 入金金額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 入金金額入力フィールドのJSX要素
   */
  const input_Pay_amount = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Pay_amount form.Pay_amount:${form?.Pay_amount || ""} params:`, params);
    return formProps.input("Pay_amount", { label: "入金金額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 金融機関 PMM_Financesの各企業Seqnum入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 金融機関 PMM_Financesの各企業Seqnum入力フィールドのJSX要素
   */
  const input_Pay_Finance = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Pay_Finance form.Pay_Finance:${form?.Pay_Finance || ""} params:`, params);
    return formProps.input("Pay_Finance", { label: "金融機関 PMM_Financesの各企業Seqnum", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 金融機関名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 金融機関名入力フィールドのJSX要素
   */
  const input_Pay_FIName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Pay_FIName form.Pay_FIName:${form?.Pay_FIName || ""} params:`, params);
    return formProps.input("Pay_FIName", { label: "金融機関名", maxlength: 80, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 金融機関支店名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 金融機関支店名入力フィールドのJSX要素
   */
  const input_Pay_FIShiten = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Pay_FIShiten form.Pay_FIShiten:${form?.Pay_FIShiten || ""} params:`, params);
    return formProps.input("Pay_FIShiten", { label: "金融機関支店名", maxlength: 80, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 口座種別入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 口座種別入力フィールドのJSX要素
   */
  const input_Pay_KouzaKind = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Pay_KouzaKind form.Pay_KouzaKind:${form?.Pay_KouzaKind || ""} params:`, params);
    return formProps.input("Pay_KouzaKind", { label: "口座種別", maxlength: 6, is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 口座番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 口座番号入力フィールドのJSX要素
   */
  const input_Pay_KouzaNo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Pay_KouzaNo form.Pay_KouzaNo:${form?.Pay_KouzaNo || ""} params:`, params);
    return formProps.input("Pay_KouzaNo", { label: "口座番号", maxlength: 20, is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 手形種類/番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 手形種類/番号入力フィールドのJSX要素
   */
  const input_Pay_Tegata = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Pay_Tegata form.Pay_Tegata:${form?.Pay_Tegata || ""} params:`, params);
    return formProps.input("Pay_Tegata", { label: "手形種類/番号", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 科目入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 科目入力フィールドのJSX要素
   */
  const input_Kamoku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Kamoku form.Kamoku:${form?.Kamoku || ""} params:`, params);
    return formProps.input("Kamoku", { label: "科目", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 科目その他入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 科目その他入力フィールドのJSX要素
   */
  const input_Kamoku_Sonota = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Kamoku_Sonota form.Kamoku_Sonota:${form?.Kamoku_Sonota || ""} params:`, params);
    return formProps.input("Kamoku_Sonota", { label: "科目その他", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 請求金額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 請求金額入力フィールドのJSX要素
   */
  const input_DealPenalty = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_DealPenalty form.DealPenalty:${form?.DealPenalty || ""} params:`, params);
    return formProps.input("DealPenalty", { label: "請求金額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 税額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 税額入力フィールドのJSX要素
   */
  const input_ZeiGaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ZeiGaku form.ZeiGaku:${form?.ZeiGaku || ""} params:`, params);
    return formProps.input("ZeiGaku", { label: "税額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 繰越額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 繰越額入力フィールドのJSX要素
   */
  const input_Kurikoshi_Gaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Kurikoshi_Gaku form.Kurikoshi_Gaku:${form?.Kurikoshi_Gaku || ""} params:`, params);
    return formProps.input("Kurikoshi_Gaku", { label: "繰越額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 数量入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 数量入力フィールドのJSX要素
   */
  const input_Suryo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Suryo form.Suryo:${form?.Suryo || ""} params:`, params);
    return formProps.input("Suryo", { label: "数量", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 送料合計入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 送料合計入力フィールドのJSX要素
   */
  const input_Soryo_Gaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Soryo_Gaku form.Soryo_Gaku:${form?.Soryo_Gaku || ""} params:`, params);
    return formProps.input("Soryo_Gaku", { label: "送料合計", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", maxLength: 10, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 組合員購入総額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 組合員購入総額入力フィールドのJSX要素
   */
  const input_SouGokei_Gaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_SouGokei_Gaku form.SouGokei_Gaku:${form?.SouGokei_Gaku || ""} params:`, params);
    return formProps.input("SouGokei_Gaku", { label: "組合員購入総額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 値引額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 値引額入力フィールドのJSX要素
   */
  const input_Nebiki_Gaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Nebiki_Gaku form.Nebiki_Gaku:${form?.Nebiki_Gaku || ""} params:`, params);
    return formProps.input("Nebiki_Gaku", { label: "値引額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 値引送料額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 値引送料額入力フィールドのJSX要素
   */
  const input_Nebiki_Soryo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Nebiki_Soryo form.Nebiki_Soryo:${form?.Nebiki_Soryo || ""} params:`, params);
    return formProps.input("Nebiki_Soryo", { label: "値引送料額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 値引税額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 値引税額入力フィールドのJSX要素
   */
  const input_Nebiki_Zei = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Nebiki_Zei form.Nebiki_Zei:${form?.Nebiki_Zei || ""} params:`, params);
    return formProps.input("Nebiki_Zei", { label: "値引税額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 値引送料税額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 値引送料税額入力フィールドのJSX要素
   */
  const input_Nebiki_SoryoZei = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Nebiki_SoryoZei form.Nebiki_SoryoZei:${form?.Nebiki_SoryoZei || ""} params:`, params);
    return formProps.input("Nebiki_SoryoZei", { label: "値引送料税額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 見積依頼の部品レコードID入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 見積依頼の部品レコードID入力フィールドのJSX要素
   */
  const input_PRID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_PRID form.PRID:${form?.PRID || ""} params:`, params);
    return formProps.input("PRID", { label: "見積依頼の部品レコードID", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 見積回答のレコードID入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 見積回答のレコードID入力フィールドのJSX要素
   */
  const input_RAID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_RAID form.RAID:${form?.RAID || ""} params:`, params);
    return formProps.input("RAID", { label: "見積回答のレコードID", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * DMD_PartsAnswers.id入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} DMD_PartsAnswers.id入力フィールドのJSX要素
   */
  const input_PAID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_PAID form.PAID:${form?.PAID || ""} params:`, params);
    return formProps.input("PAID", { label: "DMD_PartsAnswers.id", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 4CD入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 4CD入力フィールドのJSX要素
   */
  const input_4CD = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_4CD form.4CD:${form["4CD"] || ""} params:`, params);
    return formProps.input("4CD", { label: "4CD", maxlength: 4, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 購入側企業ID入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 購入側企業ID入力フィールドのJSX要素
   */
  const input_Buy_CorpID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Buy_CorpID form.Buy_CorpID:${form?.Buy_CorpID || ""} params:`, params);
    return formProps.input("Buy_CorpID", { label: "購入側企業ID", maxlength: 12, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 販売側企業ID入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 販売側企業ID入力フィールドのJSX要素
   */
  const input_Sell_CorpID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sell_CorpID form.Sell_CorpID:${form?.Sell_CorpID || ""} params:`, params);
    return formProps.input("Sell_CorpID", { label: "販売側企業ID", maxlength: 12, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 購入時商品価格入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 購入時商品価格入力フィールドのJSX要素
   */
  const input_SellPrice = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_SellPrice form.SellPrice:${form?.SellPrice || ""} params:`, params);
    return formProps.input("SellPrice", { label: "購入時商品価格", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 購入時送料価格入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 購入時送料価格入力フィールドのJSX要素
   */
  const input_SellSoryo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_SellSoryo form.SellSoryo:${form?.SellSoryo || ""} params:`, params);
    return formProps.input("SellSoryo", { label: "購入時送料価格", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 上乗せ価格入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 上乗せ価格入力フィールドのJSX要素
   */
  const input_ViewPrice = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ViewPrice form.ViewPrice:${form?.ViewPrice || ""} params:`, params);
    return formProps.input("ViewPrice", { label: "上乗せ価格", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 上乗せ送料入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 上乗せ送料入力フィールドのJSX要素
   */
  const input_ViewSoryo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ViewSoryo form.ViewSoryo:${form?.ViewSoryo || ""} params:`, params);
    return formProps.input("ViewSoryo", { label: "上乗せ送料", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 取引価格入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 取引価格入力フィールドのJSX要素
   */
  const input_DealPrice = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_DealPrice form.DealPrice:${form?.DealPrice || ""} params:`, params);
    return formProps.input("DealPrice", { ...params, label: "取引価格", isNumberOnly: true, isMinus: true, maxlength: 10, is_standard, autoComplete: "on", style: { ...baseStyle, ...params.style } }, is_debug);
  };
  /**
   * 取引送料入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 取引送料入力フィールドのJSX要素
   */
  const input_DealCarry = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_DealCarry form.DealCarry:${form?.DealCarry || ""} params:`, params);
    return formProps.input("DealCarry", { ...params, label: "送料", isNumberOnly: true, isMinus: true, maxlength: 10, is_standard, autoComplete: "on", style: { ...baseStyle, ...params.style } }, is_debug);
  };
  /**
   * 上乗せ返品値引価格入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 上乗せ返品値引価格入力フィールドのJSX要素
   */
  const input_DealPriceSK = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_DealPriceSK form.DealPriceSK:${form?.DealPriceSK || ""} params:`, params);
    return formProps.input("DealPriceSK", { label: "上乗せ返品値引価格", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 上乗せ返品値引送料入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 上乗せ返品値引送料入力フィールドのJSX要素
   */
  const input_DealCarrySK = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_DealCarrySK form.DealCarrySK:${form?.DealCarrySK || ""} params:`, params);
    return formProps.input("DealCarrySK", { label: "上乗せ返品値引送料", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 送料税額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 送料税額入力フィールドのJSX要素
   */
  const input_SoryoZeiGaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_SoryoZeiGaku form.SoryoZeiGaku:${form?.SoryoZeiGaku || ""} params:`, params);
    return formProps.input("SoryoZeiGaku", { label: "送料税額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 上乗せ税額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 上乗せ税額入力フィールドのJSX要素
   */
  const input_ViewZei = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ViewZei form.ViewZei:${form?.ViewZei || ""} params:`, params);
    return formProps.input("ViewZei", { label: "上乗せ税額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 上乗せ送料税額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 上乗せ送料税額入力フィールドのJSX要素
   */
  const input_ViewSoryoZei = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ViewSoryoZei form.ViewSoryoZei:${form?.ViewSoryoZei || ""} params:`, params);
    return formProps.input("ViewSoryoZei", { label: "上乗せ送料税額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * Movi手数料額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} Movi手数料額入力フィールドのJSX要素
   */
  const input_MoviGaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_MoviGaku form.MoviGaku:${form?.MoviGaku || ""} params:`, params);
    return formProps.input("MoviGaku", { label: "Movi手数料額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 販売購入フラグ 2:注文確定 5:発送済 8:値引 9:返品入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 販売購入フラグ 2:注文確定 5:発送済 8:値引 9:返品入力フィールドのJSX要素
   */
  const input_Buysell_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Buysell_flg form.Buysell_flg:${form?.Buysell_flg || ""} params:`, params);
    return formProps.input("Buysell_flg", { label: "販売購入フラグ 2:注文確定 5:発送済 8:値引 9:返品", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 手数料除外フラグ 1=除外する入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 手数料除外フラグ 1=除外する入力フィールドのJSX要素
   */
  const input_Tesuryo_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Tesuryo_flg form.Tesuryo_flg:${form?.Tesuryo_flg || ""} params:`, params);
    return formProps.input("Tesuryo_flg", { label: "手数料除外フラグ 1=除外する", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 別締め日用締処理フラグ 1=商工組合(15日締め) 他=締め日入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 別締め日用締処理フラグ 1=商工組合(15日締め) 他=締め日入力フィールドのJSX要素
   */
  const input_SBSK_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_SBSK_flg form.SBSK_flg:${form?.SBSK_flg || ""} params:`, params);
    return formProps.input("SBSK_flg", { label: "別締め日用締処理フラグ 1=商工組合(15日締め) 他=締め日", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * トークン文字列入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} トークン文字列入力フィールドのJSX要素
   */
  const input_TokenStr = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_TokenStr form.TokenStr:${form?.TokenStr || ""} params:`, params);
    return formProps.input("TokenStr", { label: "トークン文字列", maxlength: 256, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 入力フィールドのJSX要素
   */
  const input_UserID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_UserID form.UserID:${form?.UserID || ""} params:`, params);
    return formProps.input("UserID", { label: "", maxlength: 16, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 入力フィールドのJSX要素
   */
  const input_Pswd = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Pswd form.Pswd:${form?.Pswd || ""} params:`, params);
    return formProps.input("Pswd", { label: "", maxlength: 100, is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 都道府県名称入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 都道府県名称入力フィールドのJSX要素
   */
  const input_AreaName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_AreaName form.AreaName:${form?.AreaName || ""} params:`, params);
    return formProps.input("AreaName", { label: "都道府県名称", maxlength: 5, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * キーワード入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ（label, style, size など）
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} キーワード入力フィールドのJSX要素
   */
  const input_KeyWord = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_KeyWord form.KeyWord:${form?.KeyWord || ""} params:`, params);
    return formProps.input("KeyWord", { label: "キーワード", maxlength: 200, is_standard, autoComplete: "on", style: { ...baseStyle, width: "240px" }, ...params }, is_debug);
  };
  /**
   * グループ・団体・支部名称入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} グループ・団体・支部名称入力フィールドのJSX要素
   */
  const input_GroupName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_GroupName form.GroupName:${form?.GroupName || ""} params:`, params);
    return formProps.input("GroupName", { label: "グループ・団体・支部名称", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 親団体のID　NULL:親入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 親団体のID　NULL:親入力フィールドのJSX要素
   */
  const input_Oya_ID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Oya_ID form.Oya_ID:${form?.Oya_ID || ""} params:`, params);
    return formProps.input("Oya_ID", { label: "親団体のID　NULL:親", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 郵便番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 郵便番号入力フィールドのJSX要素
   */
  const input_ZipCode1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ZipCode1 form.ZipCode1:${form?.ZipCode1 || ""} params:`, params);
    return formProps.input("ZipCode1", { label: "郵便番号", maxlength: 3, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  const input_ZipCode2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ZipCode2 form.ZipCode2:${form?.ZipCode2 || ""} params:`, params);
    return formProps.input("ZipCode2", { label: "郵便番号", maxlength: 4, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 都道府県名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 都道府県名入力フィールドのJSX要素
   */
  const input_Todofuken = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Todofuken form.Todofuken:${form?.Todofuken || ""} params:`, params);
    return formProps.input("Todofuken", { label: "都道府県名", maxlength: 8, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 住所1入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 住所1入力フィールドのJSX要素
   */
  const input_Jyusho1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Jyusho1 form.Jyusho1:${form?.Jyusho1 || ""} params:`, params);
    return formProps.input("Jyusho1", { label: "住所1", maxlength: 80, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 住所2入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 住所2入力フィールドのJSX要素
   */
  const input_Jyusho2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Jyusho2 form.Jyusho2:${form?.Jyusho2 || ""} params:`, params);
    return formProps.input("Jyusho2", { label: "住所2", maxlength: 80, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 住所3入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 住所3入力フィールドのJSX要素
   */
  const input_Jyusho3 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Jyusho3 form.Jyusho3:${form?.Jyusho3 || ""} params:`, params);
    return formProps.input("Jyusho3", { label: "住所3", maxlength: 160, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 経理用FAX番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 経理用FAX番号入力フィールドのJSX要素
   */
  const input_Fax2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Fax2 form.Fax2:${form?.Fax2 || ""} params:`, params);
    return formProps.input("Fax2", { label: "経理用FAX番号", maxlength: 30, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 金融機関名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 金融機関名入力フィールドのJSX要素
   */
  const input_FIName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_FIName form.FIName:${form?.FIName || ""} params:`, params);
    return formProps.input("FIName", { label: "金融機関名", maxlength: 80, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 金融機関支店名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 金融機関支店名入力フィールドのJSX要素
   */
  const input_FIShiten = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_FIShiten form.FIShiten:${form?.FIShiten || ""} params:`, params);
    return formProps.input("FIShiten", { label: "金融機関支店名", maxlength: 140, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 口座種別入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 口座種別入力フィールドのJSX要素
   */
  const input_KouzaKind = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_KouzaKind form.KouzaKind:${form?.KouzaKind || ""} params:`, params);
    return formProps.input("KouzaKind", { label: "口座種別", maxlength: 6, is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 口座番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 口座番号入力フィールドのJSX要素
   */
  const input_KouzaNo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_KouzaNo form.KouzaNo:${form?.KouzaNo || ""} params:`, params);
    return formProps.input("KouzaNo", { label: "口座番号", maxlength: 20, is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 口座名義入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 口座名義入力フィールドのJSX要素
   */
  const input_Meiginin = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Meiginin form.Meiginin:${form?.Meiginin || ""} params:`, params);
    return formProps.input("Meiginin", { label: "口座名義", maxlength: 200, is_standard, style: baseStyle, ...params }, is_debug);
  };
  /**
   * 販売手数料率(生産者側のみ)入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 販売手数料率(生産者側のみ)入力フィールドのJSX要素
   */
  const input_Tesuryo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Tesuryo form.Tesuryo:${form?.Tesuryo || ""} params:`, params);
    return formProps.input("Tesuryo", { label: "販売手数料率(生産者側のみ)", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 購入C/B率(整備鈑金側のみ)入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 購入C/B率(整備鈑金側のみ)入力フィールドのJSX要素
   */
  const input_CashBack = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CashBack form.CashBack:${form?.CashBack || ""} params:`, params);
    return formProps.input("CashBack", { label: "購入C/B率(整備鈑金側のみ)", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * C/B比率入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} C/B比率入力フィールドのJSX要素
   */
  const input_CashBackRatio = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CashBackRatio form.CashBackRatio:${form?.CashBackRatio || ""} params:`, params);
    return formProps.input("CashBackRatio", { label: "C/B比率", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 締め日入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 締め日入力フィールドのJSX要素
   */
  const input_Shimebi = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shimebi form.Shimebi:${form?.Shimebi || ""} params:`, params);
    return formProps.input("Shimebi", { label: "締め日", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 支払日入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 支払日入力フィールドのJSX要素
   */
  const input_Shiharaibi = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shiharaibi form.Shiharaibi:${form?.Shiharaibi || ""} params:`, params);
    return formProps.input("Shiharaibi", { label: "支払日", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * ロゴファイル名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} ロゴファイル名入力フィールドのJSX要素
   */
  const input_LogoFileName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_LogoFileName form.LogoFileName:${form?.LogoFileName || ""} params:`, params);
    return formProps.input("LogoFileName", { label: "ロゴファイル名", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 規約ファイル名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 規約ファイル名入力フィールドのJSX要素
   */
  const input_AgreementFileName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_AgreementFileName form.AgreementFileName:${form?.AgreementFileName || ""} params:`, params);
    return formProps.input("AgreementFileName", { label: "規約ファイル名", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * マニュアルダウンロード用ファイル名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} マニュアルダウンロード用ファイル名入力フィールドのJSX要素
   */
  const input_ManualDLFileName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ManualDLFileName form.ManualDLFileName:${form?.ManualDLFileName || ""} params:`, params);
    return formProps.input("ManualDLFileName", { label: "マニュアルダウンロード用ファイル名", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 規約ダウンロード用ファイル名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 規約ダウンロード用ファイル名入力フィールドのJSX要素
   */
  const input_AgreementDLFileName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_AgreementDLFileName form.AgreementDLFileName:${form?.AgreementDLFileName || ""} params:`, params);
    return formProps.input("AgreementDLFileName", { label: "規約ダウンロード用ファイル名", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 更新情報ファイル名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 更新情報ファイル名入力フィールドのJSX要素
   */
  const input_UpdateFileName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_UpdateFileName form.UpdateFileName:${form?.UpdateFileName || ""} params:`, params);
    return formProps.input("UpdateFileName", { label: "更新情報ファイル名", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 本部計算書出力フラグ 1:出力する入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 本部計算書出力フラグ 1:出力する入力フィールドのJSX要素
   */
  const input_Keisansho_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Keisansho_flg form.Keisansho_flg:${form?.Keisansho_flg || ""} params:`, params);
    return formProps.input("Keisansho_flg", { label: "本部計算書出力フラグ 1:出力する", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌販売表示フラグ 1=非表示入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌販売表示フラグ 1=非表示入力フィールドのJSX要素
   */
  const input_CarView_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CarView_flg form.CarView_flg:${form?.CarView_flg || ""} params:`, params);
    return formProps.input("CarView_flg", { label: "車輌販売表示フラグ 1=非表示", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * トラック王国バナー表示可否 1:可入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} トラック王国バナー表示可否 1:可入力フィールドのJSX要素
   */
  const input_NentrysFlg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_NentrysFlg form.NentrysFlg:${form?.NentrysFlg || ""} params:`, params);
    return formProps.input("NentrysFlg", { label: "トラック王国バナー表示可否 1:可", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 会員種別入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 会員種別入力フィールドのJSX要素
   */
  const input_CorpKind = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CorpKind form.CorpKind:${form?.CorpKind || ""} params:`, params);
    return formProps.input("CorpKind", { label: "会員種別", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 業種名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 業種名入力フィールドのJSX要素
   */
  const input_GyoshuName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_GyoshuName form.GyoshuName:${form?.GyoshuName || ""} params:`, params);
    return formProps.input("GyoshuName", { label: "業種名", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：書類有無 1:有 5:どちらでも 9:無入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：書類有無 1:有 5:どちらでも 9:無入力フィールドのJSX要素
   */
  const input_C_Shorui_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_Shorui_flg form.C_Shorui_flg:${form?.C_Shorui_flg || ""} params:`, params);
    return formProps.input("C_Shorui_flg", { label: "車輌：書類有無 1:有 5:どちらでも 9:無", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 引取可能日入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 引取可能日入力フィールドのJSX要素
   */
  const input_C_HikitoriKanoubi = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_HikitoriKanoubi form.C_HikitoriKanoubi:${form?.C_HikitoriKanoubi || ""} params:`, params);
    return formProps.input("C_HikitoriKanoubi", { label: "引取可能日", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：入札金額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：入札金額入力フィールドのJSX要素
   */
  const input_C_NyusatsuKingaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_NyusatsuKingaku form.C_NyusatsuKingaku:${form?.C_NyusatsuKingaku || ""} params:`, params);
    return formProps.input("C_NyusatsuKingaku", { label: "車輌：入札金額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 発注フラグ　0:入札済　2:成約　5:引渡済　8:値引　9:返品入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 発注フラグ　0:入札済　2:成約　5:引渡済　8:値引　9:返品入力フィールドのJSX要素
   */
  const input_Hacchu_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Hacchu_flg form.Hacchu_flg:${form?.Hacchu_flg || ""} params:`, params);
    return formProps.input("Hacchu_flg", { label: "発注フラグ　0:入札済　2:成約　5:引渡済　8:値引　9:返品", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：リサイクル有無 1:有 9:無入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：リサイクル有無 1:有 9:無入力フィールドのJSX要素
   */
  const input_C_Recycle_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_Recycle_flg form.C_Recycle_flg:${form?.C_Recycle_flg || ""} params:`, params);
    return formProps.input("C_Recycle_flg", { label: "車輌：リサイクル有無 1:有 9:無", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：リサイクル料金入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：リサイクル料金入力フィールドのJSX要素
   */
  const input_C_Recycle_price = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_Recycle_price form.C_Recycle_price:${form?.C_Recycle_price || ""} params:`, params);
    return formProps.input("C_Recycle_price", { label: "車輌：リサイクル料金", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：車検残入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：車検残入力フィールドのJSX要素
   */
  const input_C_ShakenZan = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_ShakenZan form.C_ShakenZan:${form?.C_ShakenZan || ""} params:`, params);
    return formProps.input("C_ShakenZan", { label: "車輌：車検残", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：車輌保管場所 都道府県入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：車輌保管場所 都道府県入力フィールドのJSX要素
   */
  const input_C_HokanbashoT = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_HokanbashoT form.C_HokanbashoT:${form?.C_HokanbashoT || ""} params:`, params);
    return formProps.input("C_HokanbashoT", { label: "車輌：車輌保管場所 都道府県", maxlength: 5, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：車輌保管場所 市区町村入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：車輌保管場所 市区町村入力フィールドのJSX要素
   */
  const input_C_HokanbashoP = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_HokanbashoP form.C_HokanbashoP:${form?.C_HokanbashoP || ""} params:`, params);
    return formProps.input("C_HokanbashoP", { label: "車輌：車輌保管場所 市区町村", maxlength: 30, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：車輌保管場所 町名番地建物名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：車輌保管場所 町名番地建物名入力フィールドのJSX要素
   */
  const input_C_HokanbashoS = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_HokanbashoS form.C_HokanbashoS:${form?.C_HokanbashoS || ""} params:`, params);
    return formProps.input("C_HokanbashoS", { label: "車輌：車輌保管場所 町名番地建物名", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：所在地名称入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：所在地名称入力フィールドのJSX要素
   */
  const input_C_HokanShozaiName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_HokanShozaiName form.C_HokanShozaiName:${form?.C_HokanShozaiName || ""} params:`, params);
    return formProps.input("C_HokanShozaiName", { label: "車輌：所在地名称", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：担当者入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：担当者入力フィールドのJSX要素
   */
  const input_C_HokanShozaiTanto = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_HokanShozaiTanto form.C_HokanShozaiTanto:${form?.C_HokanShozaiTanto || ""} params:`, params);
    return formProps.input("C_HokanShozaiTanto", { label: "車輌：担当者", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：保管場所電話番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：保管場所電話番号入力フィールドのJSX要素
   */
  const input_C_HokanShozaiTel = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_HokanShozaiTel form.C_HokanShozaiTel:${form?.C_HokanShozaiTel || ""} params:`, params);
    return formProps.input("C_HokanShozaiTel", { label: "車輌：保管場所電話番号", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：保管場所備考入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：保管場所備考入力フィールドのJSX要素
   */
  const input_C_HokanShozaiBikou = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_HokanShozaiBikou form.C_HokanShozaiBikou:${form?.C_HokanShozaiBikou || ""} params:`, params);
    return formProps.input("C_HokanShozaiBikou", { label: "車輌：保管場所備考", maxlength: 300, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：車歴 1:自家用 2:レンタカー入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：車歴 1:自家用 2:レンタカー入力フィールドのJSX要素
   */
  const input_C_Shareki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_Shareki form.C_Shareki:${form?.C_Shareki || ""} params:`, params);
    return formProps.input("C_Shareki", { label: "車輌：車歴 1:自家用 2:レンタカー", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：走行距離入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：走行距離入力フィールドのJSX要素
   */
  const input_C_Soukou = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_Soukou form.C_Soukou:${form?.C_Soukou || ""} params:`, params);
    return formProps.input("C_Soukou", { label: "車輌：走行距離", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：年式入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：年式入力フィールドのJSX要素
   */
  const input_C_Nenshiki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_Nenshiki form.C_Nenshiki:${form?.C_Nenshiki || ""} params:`, params);
    return formProps.input("C_Nenshiki", { label: "車輌：年式", maxlength: 6, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：修復歴入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：修復歴入力フィールドのJSX要素
   */
  const input_C_Shufuku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_Shufuku form.C_Shufuku:${form?.C_Shufuku || ""} params:`, params);
    return formProps.input("C_Shufuku", { label: "車輌：修復歴", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：登録番号地名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：登録番号地名入力フィールドのJSX要素
   */
  const input_C_TourokuChimei = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_TourokuChimei form.C_TourokuChimei:${form?.C_TourokuChimei || ""} params:`, params);
    return formProps.input("C_TourokuChimei", { label: "車輌：登録番号地名", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：登録番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：登録番号入力フィールドのJSX要素
   */
  const input_C_Touroku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_Touroku form.C_Touroku:${form?.C_Touroku || ""} params:`, params);
    return formProps.input("C_Touroku", { label: "車輌：登録番号", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：所有者名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：所有者名入力フィールドのJSX要素
   */
  const input_C_Shoyusha = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_Shoyusha form.C_Shoyusha:${form?.C_Shoyusha || ""} params:`, params);
    return formProps.input("C_Shoyusha", { label: "車輌：所有者名", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：損傷　前部入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　前部入力フィールドのJSX要素
   */
  const input_C_Sonshou01 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_Sonshou01 form.C_Sonshou01:${form?.C_Sonshou01 || ""} params:`, params);
    return formProps.input("C_Sonshou01", { label: "車輌：損傷　前部", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：損傷　後部入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　後部入力フィールドのJSX要素
   */
  const input_C_Sonshou02 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_Sonshou02 form.C_Sonshou02:${form?.C_Sonshou02 || ""} params:`, params);
    return formProps.input("C_Sonshou02", { label: "車輌：損傷　後部", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：損傷　右側入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　右側入力フィールドのJSX要素
   */
  const input_C_Sonshou03 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_Sonshou03 form.C_Sonshou03:${form?.C_Sonshou03 || ""} params:`, params);
    return formProps.input("C_Sonshou03", { label: "車輌：損傷　右側", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：損傷　左側入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　左側入力フィールドのJSX要素
   */
  const input_C_Sonshou04 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_Sonshou04 form.C_Sonshou04:${form?.C_Sonshou04 || ""} params:`, params);
    return formProps.input("C_Sonshou04", { label: "車輌：損傷　左側", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：損傷　ルーフ入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　ルーフ入力フィールドのJSX要素
   */
  const input_C_Sonshou05 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_Sonshou05 form.C_Sonshou05:${form?.C_Sonshou05 || ""} params:`, params);
    return formProps.input("C_Sonshou05", { label: "車輌：損傷　ルーフ", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：損傷　盗難入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　盗難入力フィールドのJSX要素
   */
  const input_C_Sonshou06 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_Sonshou06 form.C_Sonshou06:${form?.C_Sonshou06 || ""} params:`, params);
    return formProps.input("C_Sonshou06", { label: "車輌：損傷　盗難", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：損傷　水没入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　水没入力フィールドのJSX要素
   */
  const input_C_Sonshou07 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_Sonshou07 form.C_Sonshou07:${form?.C_Sonshou07 || ""} params:`, params);
    return formProps.input("C_Sonshou07", { label: "車輌：損傷　水没", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：損傷　炎上入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　炎上入力フィールドのJSX要素
   */
  const input_C_Sonshou08 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_Sonshou08 form.C_Sonshou08:${form?.C_Sonshou08 || ""} params:`, params);
    return formProps.input("C_Sonshou08", { label: "車輌：損傷　炎上", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：損傷　エアバッグ展開入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　エアバッグ展開入力フィールドのJSX要素
   */
  const input_C_Sonshou09 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_Sonshou09 form.C_Sonshou09:${form?.C_Sonshou09 || ""} params:`, params);
    return formProps.input("C_Sonshou09", { label: "車輌：損傷　エアバッグ展開", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：損傷　横転入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：損傷　横転入力フィールドのJSX要素
   */
  const input_C_Sonshou10 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_Sonshou10 form.C_Sonshou10:${form?.C_Sonshou10 || ""} params:`, params);
    return formProps.input("C_Sonshou10", { label: "車輌：損傷　横転", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：足回り 1:不明 5:OK 9:NG入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：足回り 1:不明 5:OK 9:NG入力フィールドのJSX要素
   */
  const input_C_Ashimawari = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_Ashimawari form.C_Ashimawari:${form?.C_Ashimawari || ""} params:`, params);
    return formProps.input("C_Ashimawari", { label: "車輌：足回り 1:不明 5:OK 9:NG", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：足回りNGの場合 左前 1:有 9:無入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：足回りNGの場合 左前 1:有 9:無入力フィールドのJSX要素
   */
  const input_C_AshimawariNG1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_AshimawariNG1 form.C_AshimawariNG1:${form?.C_AshimawariNG1 || ""} params:`, params);
    return formProps.input("C_AshimawariNG1", { label: "車輌：足回りNGの場合 左前 1:有 9:無", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：足回りNGの場合 右前 1:有 9:無入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：足回りNGの場合 右前 1:有 9:無入力フィールドのJSX要素
   */
  const input_C_AshimawariNG2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_AshimawariNG2 form.C_AshimawariNG2:${form?.C_AshimawariNG2 || ""} params:`, params);
    return formProps.input("C_AshimawariNG2", { label: "車輌：足回りNGの場合 右前 1:有 9:無", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：足回りNGの場合 左後 1:有 9:無入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：足回りNGの場合 左後 1:有 9:無入力フィールドのJSX要素
   */
  const input_C_AshimawariNG3 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_AshimawariNG3 form.C_AshimawariNG3:${form?.C_AshimawariNG3 || ""} params:`, params);
    return formProps.input("C_AshimawariNG3", { label: "車輌：足回りNGの場合 左後 1:有 9:無", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：足回りNGの場合 右後 1:有 9:無入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：足回りNGの場合 右後 1:有 9:無入力フィールドのJSX要素
   */
  const input_C_AshimawariNG4 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_AshimawariNG4 form.C_AshimawariNG4:${form?.C_AshimawariNG4 || ""} params:`, params);
    return formProps.input("C_AshimawariNG4", { label: "車輌：足回りNGの場合 右後 1:有 9:無", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：エンジン状態入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：エンジン状態入力フィールドのJSX要素
   */
  const input_C_EngineJyotai = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_EngineJyotai form.C_EngineJyotai:${form?.C_EngineJyotai || ""} params:`, params);
    return formProps.input("C_EngineJyotai", { label: "車輌：エンジン状態", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：鍵の有無入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：鍵の有無入力フィールドのJSX要素
   */
  const input_C_KeyJyotai = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_KeyJyotai form.C_KeyJyotai:${form?.C_KeyJyotai || ""} params:`, params);
    return formProps.input("C_KeyJyotai", { label: "車輌：鍵の有無", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：引取方法入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：引取方法入力フィールドのJSX要素
   */
  const input_C_Hikitorihouhou = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_Hikitorihouhou form.C_Hikitorihouhou:${form?.C_Hikitorihouhou || ""} params:`, params);
    return formProps.input("C_Hikitorihouhou", { label: "車輌：引取方法", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：車輌看板入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：車輌看板入力フィールドのJSX要素
   */
  const input_C_SharyoKanban = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_SharyoKanban form.C_SharyoKanban:${form?.C_SharyoKanban || ""} params:`, params);
    return formProps.input("C_SharyoKanban", { label: "車輌：車輌看板", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌：希望金額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌：希望金額入力フィールドのJSX要素
   */
  const input_C_KibouKingaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_C_KibouKingaku form.C_KibouKingaku:${form?.C_KibouKingaku || ""} params:`, params);
    return formProps.input("C_KibouKingaku", { label: "車輌：希望金額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 目も入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 目も入力フィールドのJSX要素
   */
  const input_Memo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Memo form.Memo:${form?.Memo || ""} params:`, params);
    return formProps.input("Memo", { label: "目も", maxlength: 300, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 担当者入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 担当者入力フィールドのJSX要素
   */
  const input_Tanto = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Tanto form.Tanto:${form?.Tanto || ""} params:`, params);
    return formProps.input("Tanto", { label: "担当者", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * クレーム対応入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} クレーム対応入力フィールドのJSX要素
   */
  const input_Claim = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Claim form.Claim:${form?.Claim || ""} params:`, params);
    return formProps.input("Claim", { label: "クレーム対応", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 自社管理番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 自社管理番号入力フィールドのJSX要素
   */
  const input_JishaNo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_JishaNo form.JishaNo:${form?.JishaNo || ""} params:`, params);
    return formProps.input("JishaNo", { label: "自社管理番号", isNumAndAlpha: true, maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 部品純正品番入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 部品純正品番入力フィールドのJSX要素
   */
  const input_PartsJyunseiCode = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_PartsJyunseiCode form.PartsJyunseiCode:${form?.PartsJyunseiCode || ""} params:`, params);
    return formProps.input("PartsJyunseiCode", { label: "部品純正品番", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 部品種別ID入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 部品種別ID入力フィールドのJSX要素
   */
  const input_PartsKindID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_PartsKindID form.PartsKindID:${form?.PartsKindID || ""} params:`, params);
    return formProps.input("PartsKindID", { label: "部品種別ID", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 価格の評価入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 価格の評価入力フィールドのJSX要素
   */
  const input_Eval_Kakaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Eval_Kakaku form.Eval_Kakaku:${form?.Eval_Kakaku || ""} params:`, params);
    return formProps.input("Eval_Kakaku", { label: "価格の評価", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 品質の評価入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 品質の評価入力フィールドのJSX要素
   */
  const input_Eval_Hinshitsu = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Eval_Hinshitsu form.Eval_Hinshitsu:${form?.Eval_Hinshitsu || ""} params:`, params);
    return formProps.input("Eval_Hinshitsu", { label: "品質の評価", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 梱包の評価入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 梱包の評価入力フィールドのJSX要素
   */
  const input_Eval_Konpou = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Eval_Konpou form.Eval_Konpou:${form?.Eval_Konpou || ""} params:`, params);
    return formProps.input("Eval_Konpou", { label: "梱包の評価", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 納期の評価入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 納期の評価入力フィールドのJSX要素
   */
  const input_Eval_Nouki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Eval_Nouki form.Eval_Nouki:${form?.Eval_Nouki || ""} params:`, params);
    return formProps.input("Eval_Nouki", { label: "納期の評価", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 総合評価入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 総合評価入力フィールドのJSX要素
   */
  const input_Eval_Sougou = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Eval_Sougou form.Eval_Sougou:${form?.Eval_Sougou || ""} params:`, params);
    return formProps.input("Eval_Sougou", { label: "総合評価", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * ファイルパス入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} ファイルパス入力フィールドのJSX要素
   */
  const input_DocPath = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_DocPath form.DocPath:${form?.DocPath || ""} params:`, params);
    return formProps.input("DocPath", { label: "ファイルパス", maxlength: 300, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 発送登録したPAID入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 発送登録したPAID入力フィールドのJSX要素
   */
  const input_HaisoPAID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_HaisoPAID form.HaisoPAID:${form?.HaisoPAID || ""} params:`, params);
    return formProps.input("HaisoPAID", { label: "発送登録したPAID", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 発送登録した部品名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 発送登録した部品名入力フィールドのJSX要素
   */
  const input_PartsName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_PartsName form.PartsName:${form?.PartsName || ""} params:`, params);
    return formProps.input("PartsName", { label: "発送登録した部品名", maxlength: 200, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * メッセージ入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} メッセージ入力フィールドのJSX要素
   */
  const input_Message = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Message form.Message:${form?.Message || ""} params:`, params);
    return formProps.input("Message", { label: "メッセージ", maxlength: 200, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 手配種別　1=自社 2=他社入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 手配種別　1=自社 2=他社入力フィールドのJSX要素
   */
  const input_PrepKind = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_PrepKind form.PrepKind:${form?.PrepKind || ""} params:`, params);
    return formProps.input("PrepKind", { label: "手配種別　1=自社 2=他社", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 追加商品4CD入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 追加商品4CD入力フィールドのJSX要素
   */
  const input_OthParts4CD = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_OthParts4CD form.OthParts4CD:${form?.OthParts4CD || ""} params:`, params);
    return formProps.input("OthParts4CD", { label: "追加商品4CD", maxlength: 4, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 追加商品名 (PRID=0の時に有効)入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 追加商品名 (PRID=0の時に有効)入力フィールドのJSX要素
   */
  const input_OthPartsName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_OthPartsName form.OthPartsName:${form?.OthPartsName || ""} params:`, params);
    return formProps.input("OthPartsName", { label: "追加商品名 (PRID=0の時に有効)", maxlength: 200, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 追加商品その他入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 追加商品その他入力フィールドのJSX要素
   */
  const input_OthPartsSonota = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_OthPartsSonota form.OthPartsSonota:${form?.OthPartsSonota || ""} params:`, params);
    return formProps.input("OthPartsSonota", { label: "追加商品その他", maxlength: 200, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 標準価格入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 標準価格入力フィールドのJSX要素
   */
  const input_StanderdPrice = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_StanderdPrice form.StanderdPrice:${form?.StanderdPrice || ""} params:`, params);
    return formProps.input(
      "StanderdPrice",
      {
        ...params,
        label: "標準価格",
        isNumberOnly: true,
        isMinus: true,
        is_standard, autoComplete: "on",
        maxLength: 10,
        style: { ...baseStyle, textAlign: "left",  ...(params.style || {}) }, 
      },
      is_debug
    );
  };
  /**
   * 送料入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 送料入力フィールドのJSX要素
   */
  const input_Soryo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Soryo form.Soryo:${form?.Soryo || ""} params:`, params);
    return formProps.input(
      "Soryo",
      {
        ...params,
        label: "送料",
        isNumberOnly: true,
        isMinus: true,
        is_standard, autoComplete: "on",
        maxLength: 10,
        style: { ...baseStyle, textAlign: "left",  ...(params.style || {}) },
      },
      is_debug
    );
  };
  /**
   * 発送可能 1:当日 2:翌日 3:翌々日 n:n営業日以内入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 発送可能 1:当日 2:翌日 3:翌々日 n:n営業日以内入力フィールドのJSX要素
   */
  const input_Hasso = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Hasso form.Hasso:${form?.Hasso || ""} params:`, params);
    return formProps.input("Hasso", { label: "発送可能 1:当日 2:翌日 3:翌々日 n:n営業日以内", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * カラー番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} カラー番号入力フィールドのJSX要素
   */
  const input_ColorNo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ColorNo form.ColorNo:${form?.ColorNo || ""} params:`, params);
    return formProps.input("ColorNo", { label: "カラー番号", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * カラー名称入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} カラー名称入力フィールドのJSX要素
   */
  const input_ColorName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ColorName form.ColorName:${form?.ColorName || ""} params:`, params);
    return formProps.input("ColorName", { label: "カラー名称", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * トリム番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} トリム番号入力フィールドのJSX要素
   */
  const input_TrimNo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_TrimNo form.TrimNo:${form?.TrimNo || ""} params:`, params);
    return formProps.input("TrimNo", { label: "トリム番号", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 引渡し区分 1:発送 2:店頭渡 3:自社配送入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 引渡し区分 1:発送 2:店頭渡 3:自社配送入力フィールドのJSX要素
   */
  const input_Haiso_kubun = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Haiso_kubun form.Haiso_kubun:${form?.Haiso_kubun || ""} params:`, params);
    return formProps.input("Haiso_kubun", { label: "引渡し区分 1:発送 2:店頭渡 3:自社配送", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 運送会社名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 運送会社名入力フィールドのJSX要素
   */
  const input_Haiso_Corp = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Haiso_Corp form.Haiso_Corp:${form?.Haiso_Corp || ""} params:`, params);
    return formProps.input("Haiso_Corp", { label: "運送会社名", maxlength: 60, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 送り状番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 送り状番号入力フィールドのJSX要素
   */
  const input_Haiso_denpyo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Haiso_denpyo form.Haiso_denpyo:${form?.Haiso_denpyo || ""} params:`, params);
    return formProps.input("Haiso_denpyo", { label: "送り状番号", maxlength: 60, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 発送時備考入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 発送時備考入力フィールドのJSX要素
   */
  const input_HassouBikou = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_HassouBikou form.HassouBikou:${form?.HassouBikou || ""} params:`, params);
    return formProps.input(
      "HassouBikou",
      { ...params, label: "発送メッセージ", rows: 5, multiline: true, maxRows: 10, maxlength: 100, is_standard, autoComplete: "on", style: { ...baseStyle, ...(params.style || {}) } },
      is_debug
    );
  };
  /**
   * 注文者ID入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 注文者ID入力フィールドのJSX要素
   */
  const input_Chumonsha = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Chumonsha form.Chumonsha:${form?.Chumonsha || ""} params:`, params);
    return formProps.input("Chumonsha", { label: "注文者ID", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 在庫なし回答削除フラグ 9:削除入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 在庫なし回答削除フラグ 9:削除入力フィールドのJSX要素
   */
  const input_del_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_del_flg form.del_flg:${form?.del_flg || ""} params:`, params);
    return formProps.input("del_flg", { label: "在庫なし回答削除フラグ 9:削除", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 事務局代理登録フラグ入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 事務局代理登録フラグ入力フィールドのJSX要素
   */
  const input_DairiTouroku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_DairiTouroku form.DairiTouroku:${form?.DairiTouroku || ""} params:`, params);
    return formProps.input("DairiTouroku", { label: "事務局代理登録フラグ", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * DMD_PartsRequestsのID入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} DMD_PartsRequestsのID入力フィールドのJSX要素
   */
  const input_DPR_ID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_DPR_ID form.DPR_ID:${form?.DPR_ID || ""} params:`, params);
    return formProps.input("DPR_ID", { label: "DMD_PartsRequestsのID", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 純正品番ハイフン入り入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 純正品番ハイフン入り入力フィールドのJSX要素
   */
  const input_JyunseiCode = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_JyunseiCode form.JyunseiCode:${form?.JyunseiCode || ""} params:`, params);
    return formProps.input("JyunseiCode", { label: "純正品番ハイフン入り", maxlength: 200, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 純正元品番ハイフン入り入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 純正元品番ハイフン入り入力フィールドのJSX要素
   */
  const input_JyunseiCode_M = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_JyunseiCode_M form.JyunseiCode_M:${form?.JyunseiCode_M || ""} params:`, params);
    return formProps.input("JyunseiCode_M", { label: "純正品番", maxlength: 200, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 価格入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 価格入力フィールドのJSX要素
   */
  const input_Kakaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Kakaku form.Kakaku:${form?.Kakaku || ""} params:`, params);
    return formProps.input("Kakaku", { label: "価格", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 数量入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 数量入力フィールドのJSX要素
   */
  const input_Suuryo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Suuryo form.Suuryo:${form?.Suuryo || ""} params:`, params);
    return formProps.input("Suuryo", { label: "数量", maxlength: 5, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * メーカー名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} メーカー名入力フィールドのJSX要素
   */
  const input_MakerName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_MakerName form.MakerName:${form?.MakerName || ""} params:`, params);
    return formProps.input("MakerName", { label: "メーカー名", maxlength: 5, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 始期入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 始期入力フィールドのJSX要素
   */
  const input_St = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_St form.St:${form?.St || ""} params:`, params);
    return formProps.input("St", { label: "始期", maxlength: 8, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 終期入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 終期入力フィールドのJSX要素
   */
  const input_Ed = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Ed form.Ed:${form?.Ed || ""} params:`, params);
    return formProps.input("Ed", { label: "終期", maxlength: 8, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 適用コメ入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 適用コメ入力フィールドのJSX要素
   */
  const input_Tekiyo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Tekiyo form.Tekiyo:${form?.Tekiyo || ""} params:`, params);
    return formProps.input("Tekiyo", { label: "適用コメ", maxlength: 200, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * Zwse4CD入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} Zwse4CD入力フィールドのJSX要素
   */
  const input_Zwse4CD = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Zwse4CD form.Zwse4CD:${form?.Zwse4CD || ""} params:`, params);
    return formProps.input("Zwse4CD", { label: "Zwse4CD", maxlength: 5, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 部品：部品名称入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 部品：部品名称入力フィールドのJSX要素
   */
  const input_P_PartsName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_P_PartsName form.P_PartsName:${form?.P_PartsName || ""} params:`, params);
    return formProps.input("P_PartsName", { label: "部品：部品名称", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 部品：希望部品種別1入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 部品：希望部品種別1入力フィールドのJSX要素
   */
  const input_P_PKID1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_P_PKID1 form.P_PKID1:${form?.P_PKID1 || ""} params:`, params);
    return formProps.input("P_PKID1", { label: "部品：希望部品種別1", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 部品：希望部品種別2入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 部品：希望部品種別2入力フィールドのJSX要素
   */
  const input_P_PKID2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_P_PKID2 form.P_PKID2:${form?.P_PKID2 || ""} params:`, params);
    return formProps.input("P_PKID2", { label: "部品：希望部品種別2", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 部品：希望部品種別3入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 部品：希望部品種別3入力フィールドのJSX要素
   */
  const input_P_PKID3 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_P_PKID3 form.P_PKID3:${form?.P_PKID3 || ""} params:`, params);
    return formProps.input("P_PKID3", { label: "部品：希望部品種別3", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 部品：希望部品種別4入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 部品：希望部品種別4入力フィールドのJSX要素
   */
  const input_P_PKID4 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_P_PKID4 form.P_PKID4:${form?.P_PKID4 || ""} params:`, params);
    return formProps.input("P_PKID4", { label: "部品：希望部品種別4", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 入力フィールドのJSX要素
   */
  const input_P_PKID5 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_P_PKID5 form.P_PKID5:${form?.P_PKID5 || ""} params:`, params);
    return formProps.input("P_PKID5", { label: "", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 画像パス入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 画像パス入力フィールドのJSX要素
   */
  const input_PictPath = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_PictPath form.PictPath:${form?.PictPath || ""} params:`, params);
    return formProps.input("PictPath", { label: "画像パス", maxlength: 300, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 質問番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 質問番号入力フィールドのJSX要素
   */
  const input_Q_no = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Q_no form.Q_no:${form?.Q_no || ""} params:`, params);
    return formProps.input("Q_no", { label: "質問番号", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * QAフラグ 1=質問 2=回答入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} QAフラグ 1=質問 2=回答入力フィールドのJSX要素
   */
  const input_QA_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_QA_flg form.QA_flg:${form?.QA_flg || ""} params:`, params);
    return formProps.input("QA_flg", { label: "QAフラグ 1=質問 2=回答", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 質問回答内容入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 質問回答内容入力フィールドのJSX要素
   */
  const input_QAnaiyo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_QAnaiyo form.QAnaiyo:${form?.QAnaiyo || ""} params:`, params);
    return formProps.input("QAnaiyo", { label: "質問回答内容", maxlength: 300, is_standard, autoComplete: "on", style: { ...baseStyle, ...params.style }, ...params }, is_debug);
  };
  /**
   * 未読既読フラグ 1:既読入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 未読既読フラグ 1:既読入力フィールドのJSX要素
   */
  const input_al_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_al_flg form.al_flg:${form?.al_flg || ""} params:`, params);
    return formProps.input("al_flg", { label: "未読既読フラグ 1:既読", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * PMM_CorpAccountsのid入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} PMM_CorpAccountsのid入力フィールドのJSX要素
   */
  const input_PCA_ID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_PCA_ID form.PCA_ID:${form?.PCA_ID || ""} params:`, params);
    return formProps.input("PCA_ID", { label: "PMM_CorpAccountsのid", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 親見積番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 親見積番号入力フィールドのJSX要素
   */
  const input_Oya_MitsumoriNo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Oya_MitsumoriNo form.Oya_MitsumoriNo:${form?.Oya_MitsumoriNo || ""} params:`, params);
    return formProps.input("Oya_MitsumoriNo", { label: "親見積番号", maxlength: 12, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * GenshaInfo.id入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} GenshaInfo.id入力フィールドのJSX要素
   */
  const input_GenshaID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_GenshaID form.GenshaID:${form?.GenshaID || ""} params:`, params);
    return formProps.input("GenshaID", { label: "GenshaInfo.id", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 未読既読フラグ 1=既読入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 未読既読フラグ 1=既読入力フィールドのJSX要素
   */
  const input_al_flg2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_al_flg2 form.al_flg2:${form?.al_flg2 || ""} params:`, params);
    return formProps.input("al_flg2", { label: "未読既読フラグ 1=既読", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 一覧表示フラグ NULL:表示　9:非表示入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 一覧表示フラグ NULL:表示　9:非表示入力フィールドのJSX要素
   */
  const input_view_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_view_flg form.view_flg:${form?.view_flg || ""} params:`, params);
    return formProps.input("view_flg", { label: "一覧表示フラグ NULL:表示　9:非表示", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 見積回答者ID入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 見積回答者ID入力フィールドのJSX要素
   */
  const input_Kaitosha = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Kaitosha form.Kaitosha:${form?.Kaitosha || ""} params:`, params);
    return formProps.input("Kaitosha", { label: "見積回答者ID", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 生産者側：社内用備考入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 生産者側：社内用備考入力フィールドのJSX要素
   */
  const input_BikouYZ = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_BikouYZ form.BikouYZ:${form?.BikouYZ || ""} params:`, params);
    return formProps.input("BikouYZ", { ...params, label: "社内用備考", multiline: true, maxRows: 10, rows: 4, maxlength: 1000, is_standard, autoComplete: "on", style: { ...baseStyle, ...params.style } }, is_debug);
  };
  /**
   * エリアコード入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} エリアコード入力フィールドのJSX要素
   */
  const input_AreaCode = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_AreaCode form.AreaCode:${form?.AreaCode || ""} params:`, params);
    return formProps.input("AreaCode", { label: "エリアコード", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  // @@@ 確認
  /**
   * 型式指定入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 型式指定入力フィールドのJSX要素
   */
  const input_Katashiki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Katashiki form.Katashiki:${form?.Katashiki || ""} params:`, params);
    return formProps.input("Katashiki", { label: "型式指定", maxlength: 10, is_standard, autoComplete: "on", isNumberOnly: true, isMinus: false, style: baseStyle, placeholder: "例：09212", ...params }, is_debug);
  };
  /**
   * 類別区分入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 類別区分入力フィールドのJSX要素
   */
  const input_Ruibetsu = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Ruibetsu form.Ruibetsu:${form?.Ruibetsu || ""} params:`, params);
    return formProps.input("Ruibetsu", { label: "類別区分", isNumberOnly: true, maxlength: 10, is_standard, autoComplete: "on", style: baseStyle, placeholder: "例：0020", ...params }, is_debug);
  };
  /**
   * 車台型式入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車台型式入力フィールドのJSX要素
   */
  const input_ShadaiKatashiki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ShadaiKatashiki form.ShadaiKatashiki:${form?.ShadaiKatashiki || ""} params:`, params);
    return formProps.input("ShadaiKatashiki", { label: "車台型式", maxlength: 30, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車台番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車台番号入力フィールドのJSX要素
   */
  const input_Shadaibango = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shadaibango form.Shadaibango:${form?.Shadaibango || ""} params:`, params);
    return formProps.input("Shadaibango", { label: "車台番号", maxlength: 100, isMailAddress: true, is_standard, autoComplete: "on", style: baseStyle, placeholder: "例：1025599", ...params }, is_debug);
  };
  /**
   * メーカーコード入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} メーカーコード入力フィールドのJSX要素
   */
  const input_MakerCD = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_MakerCD form.MakerCD:${form?.MakerCD || ""} params:`, params);
    return formProps.input("MakerCD", { label: "メーカーコード", maxlength: 5, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車名入力フィールドのJSX要素
   */
  const input_CarName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CarName form.CarName:${form?.CarName || ""} params:`, params);
    return formProps.input(
      "CarName",
      {
        label: "車名",
        maxlength: 100,
        size: "35",
        style: { imeMode: "active", ...baseStyle },
        is_standard, autoComplete: "on",
        placeholder: "例：ﾗｲﾌ",
        ...params,
      },
      is_debug
    );
  };
  /**
   * 関連型式入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 関連型式入力フィールドのJSX要素
   */
  const input_KanrenKatashiki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_KanrenKatashiki form.KanrenKatashiki:${form?.KanrenKatashiki || ""} params:`, params);
    return formProps.input("KanrenKatashiki", { label: "関連型式", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌型式入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌型式入力フィールドのJSX要素
   */
  const input_Sharyo_Katashiki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Katashiki form.Sharyo_Katashiki:${form?.Sharyo_Katashiki || ""} params:`, params);
    return formProps.input("Sharyo_Katashiki", { label: "車輌型式", maxlength: 30, is_standard, autoComplete: "on", style: baseStyle, placeholder: "例：GF-JB1", ...params }, is_debug);
  };
  /**
   * 年式入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 年式入力フィールドのJSX要素
   */
  const input_Sharyo_Nenshiki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Nenshiki form.Sharyo_Nenshiki:${form?.Sharyo_Nenshiki || ""} params:`, params);
    return formProps.input("Sharyo_Nenshiki", { label: "年式", maxlength: 30, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * グレード入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} グレード入力フィールドのJSX要素
   */
  const input_Sharyo_Grade = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Grade form.Sharyo_Grade:${form?.Sharyo_Grade || ""} params:`, params);
    return formProps.input("Sharyo_Grade", { label: "グレード", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * エンジン型式入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} エンジン型式入力フィールドのJSX要素
   */
  const input_Sharyo_EngineKatashiki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_EngineKatashiki form.Sharyo_EngineKatashiki:${form?.Sharyo_EngineKatashiki || ""} params:`, params);
    return formProps.input("Sharyo_EngineKatashiki", { label: "エンジン型式", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 排ガス規制入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 排ガス規制入力フィールドのJSX要素
   */
  const input_Sharyo_HaigasKisei = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_HaigasKisei form.Sharyo_HaigasKisei:${form?.Sharyo_HaigasKisei || ""} params:`, params);
    return formProps.input("Sharyo_HaigasKisei", { label: "排ガス規制", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 排気量入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 排気量入力フィールドのJSX要素
   */
  const input_Sharyo_Haikiryo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Haikiryo form.Sharyo_Haikiryo:${form?.Sharyo_Haikiryo || ""} params:`, params);
    return formProps.input("Sharyo_Haikiryo", { label: "排気量", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 燃料種別入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 燃料種別入力フィールドのJSX要素
   */
  const input_Sharyo_Ekubun = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Ekubun form.Sharyo_Ekubun:${form?.Sharyo_Ekubun || ""} params:`, params);
    return formProps.input("Sharyo_Ekubun", { label: "燃料種別", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * シフト方式入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} シフト方式入力フィールドのJSX要素
   */
  const input_Sharyo_TransMission = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_TransMission form.Sharyo_TransMission:${form?.Sharyo_TransMission || ""} params:`, params);
    return formProps.input("Sharyo_TransMission", { label: "シフト方式", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 駆動方式入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 駆動方式入力フィールドのJSX要素
   */
  const input_Sharyo_KudouHoushiki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_KudouHoushiki form.Sharyo_KudouHoushiki:${form?.Sharyo_KudouHoushiki || ""} params:`, params);
    return formProps.input("Sharyo_KudouHoushiki", { label: "駆動方式", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * カラー番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} カラー番号入力フィールドのJSX要素
   */
  const input_Sharyo_ColorNo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_ColorNo form.Sharyo_ColorNo:${form?.Sharyo_ColorNo || ""} params:`, params);
    return formProps.input("Sharyo_ColorNo", { label: "カラー番号", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * カラー名称入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} カラー名称入力フィールドのJSX要素
   */
  const input_Sharyo_Color = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Color form.Sharyo_Color:${form?.Sharyo_Color || ""} params:`, params);
    return formProps.input("Sharyo_Color", { label: "カラー名称", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * トリム番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} トリム番号入力フィールドのJSX要素
   */
  const input_Sharyo_TrimNo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_TrimNo form.Sharyo_TrimNo:${form?.Sharyo_TrimNo || ""} params:`, params);
    return formProps.input("Sharyo_TrimNo", { label: "トリム番号", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * トリム名称入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} トリム名称入力フィールドのJSX要素
   */
  const input_Sharyo_Trim = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Trim form.Sharyo_Trim:${form?.Sharyo_Trim || ""} params:`, params);
    return formProps.input("Sharyo_Trim", { label: "トリム名称", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * ハンドル入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} ハンドル入力フィールドのJSX要素
   */
  const input_Sharyo_Handle = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Handle form.Sharyo_Handle:${form?.Sharyo_Handle || ""} params:`, params);
    return formProps.input("Sharyo_Handle", { label: "ハンドル", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  const input_Sharyo_Remark_Nm = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Remark_Nm form.Sharyo_Remark_Nm:${form?.Sharyo_Remark_Nm || ""} params:`, params);
    return formProps.input("Sharyo_Remark_Nm", { label: "装備情報", maxlength: 200, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  const input_Sharyo_Body_Nm = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Body_Nm form.Sharyo_Body_Nm:${form?.Sharyo_Body_Nm || ""} params:`, params);
    return formProps.input("Sharyo_Body_Nm", { label: "ボディタイプ", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  const input_Sharyo_Sya_Type = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Sya_Type form.Sharyo_Sya_Type:${form?.Sharyo_Sya_Type || ""} params:`, params);
    return formProps.input("Sharyo_Sya_Type", { label: "車型グループ", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 依頼ユーザー名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 依頼ユーザー名入力フィールドのJSX要素
   */
  const input_I_User = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_I_User form.I_User:${form?.I_User || ""} params:`, params);
    return formProps.input("I_User", { label: "依頼ユーザー名", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 依頼ユーザー電話番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 依頼ユーザー電話番号入力フィールドのJSX要素
   */
  const input_I_UserTEL = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_I_UserTEL form.I_UserTEL:${form?.I_UserTEL || ""} params:`, params);
    return formProps.input("I_UserTEL", { label: "依頼ユーザー電話番号",isMinus: true, maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 依頼者企業名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 依頼者企業名入力フィールドのJSX要素
   */
  const input_I_Corp = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_I_Corp form.I_Corp:${form?.I_Corp || ""} params:`, params);
    return formProps.input("I_Corp", { label: "依頼者企業名", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 依頼者保険会社名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 依頼者保険会社名入力フィールドのJSX要素
   */
  const input_I_InsuranceCorp = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_I_InsuranceCorp form.I_InsuranceCorp:${form?.I_InsuranceCorp || ""} params:`, params);
    return formProps.input("I_InsuranceCorp", { label: "依頼者保険会社名", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 依頼者SC名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 依頼者SC名入力フィールドのJSX要素
   */
  const input_I_InsuranceSC = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_I_InsuranceSC form.I_InsuranceSC:${form?.I_InsuranceSC || ""} params:`, params);
    return formProps.input("I_InsuranceSC", { label: "依頼者SC名", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 依頼者損保担当者名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 依頼者損保担当者名入力フィールドのJSX要素
   */
  const input_I_InsuranceTanto = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_I_InsuranceTanto form.I_InsuranceTanto:${form?.I_InsuranceTanto || ""} params:`, params);
    return formProps.input("I_InsuranceTanto", { label: "依頼者損保担当者名", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 初年度登録年月入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 初年度登録年月入力フィールドのJSX要素
   */
  const input_Shonendo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shonendo form.Shonendo:${form?.Shonendo || ""} params:`, params);
    return formProps.input("Shonendo", { label: "初年度登録年月", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車検満了日入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車検満了日入力フィールドのJSX要素
   */
  const input_ShakenManryo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ShakenManryo form.ShakenManryo:${form?.ShakenManryo || ""} params:`, params);
    return formProps.input("ShakenManryo", { label: "車検満了日", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 登録番号1入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 登録番号1入力フィールドのJSX要素
   */
  const input_TorokuBango1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_TorokuBango1 form.TorokuBango1:${form?.TorokuBango1 || ""} params:`, params);
    return formProps.input("TorokuBango1", { label: "登録番号1", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 登録番号2入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 登録番号2入力フィールドのJSX要素
   */
  const input_TorokuBango2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_TorokuBango2 form.TorokuBango2:${form?.TorokuBango2 || ""} params:`, params);
    return formProps.input("TorokuBango2", { label: "登録番号2", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 登録番号3入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 登録番号3入力フィールドのJSX要素
   */
  const input_TorokuBango3 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_TorokuBango3 form.TorokuBango3:${form?.TorokuBango3 || ""} params:`, params);
    return formProps.input("TorokuBango3", { label: "登録番号3", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 登録番号4入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 登録番号4入力フィールドのJSX要素
   */
  const input_TorokuBango4 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_TorokuBango4 form.TorokuBango4:${form?.TorokuBango4 || ""} params:`, params);
    return formProps.input("TorokuBango4", { label: "登録番号4", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 他システム種別入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 他システム種別入力フィールドのJSX要素
   */
  const input_Oth_SysID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Oth_SysID form.Oth_SysID:${form?.Oth_SysID || ""} params:`, params);
    return formProps.input("Oth_SysID", { label: "他システム種別", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 他システム管理番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 他システム管理番号入力フィールドのJSX要素
   */
  const input_Oth_SysNo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Oth_SysNo form.Oth_SysNo:${form?.Oth_SysNo || ""} params:`, params);
    return formProps.input("Oth_SysNo", { label: "他システム管理番号", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 発送先企業名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 発送先企業名入力フィールドのJSX要素
   */
  const input_Hassosaki_Corp = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Hassosaki_Corp form.Hassosaki_Corp:${form?.Hassosaki_Corp || ""} params:`, params);
    return formProps.input("Hassosaki_Corp", { label: "発送先企業名", maxlength: 100, is_standard, autoComplete: "on", style: { ...baseStyle, ...params, width: "30em" } }, is_debug);
  };
  /**
   * 発送先担当者名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 発送先担当者名入力フィールドのJSX要素
   */
  const input_Hassosaki_Tanto = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Hassosaki_Tanto form.Hassosaki_Tanto:${form?.Hassosaki_Tanto || ""} params:`, params);
    return formProps.input("Hassosaki_Tanto", { label: "発送先担当者名", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 発送先郵便番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 発送先郵便番号入力フィールドのJSX要素
   */
  const input_Hassosaki_ZipCode1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Hassosaki_ZipCode1 form.Hassosaki_ZipCode:${form?.Hassosaki_ZipCode1 || ""} params:`, params);
    return formProps.input("Hassosaki_ZipCode1", { label: "発送先郵便番号",isNumberOnly: true, maxlength: 3, is_standard, autoComplete: "on", style: { ...baseStyle, ...params, width: "9em" } }, is_debug);
  };
  const input_Hassosaki_ZipCode2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Hassosaki_ZipCode2 form.Hassosaki_ZipCode:${form?.Hassosaki_ZipCode2 || ""} params:`, params);
    return formProps.input("Hassosaki_ZipCode2", { label: "発送先郵便番号",isNumberOnly: true, maxlength: 4, is_standard, autoComplete: "on", style: { ...baseStyle, ...params, width: "9em" } }, is_debug);
  };
  /**
   * 発送先都道府県入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 発送先都道府県入力フィールドのJSX要素
   */
  const input_Hassosaki_Todofuken = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Hassosaki_Todofuken form.Hassosaki_Todofuken:${form?.Hassosaki_Todofuken || ""} params:`, params);
    return formProps.input("Hassosaki_Todofuken", { label: "発送先都道府県", maxlength: 10, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 発送先住所入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 発送先住所入力フィールドのJSX要素
   */
  const input_Hassosaki_Address = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Hassosaki_Address form.Hassosaki_Address:${form?.Hassosaki_Address || ""} params:`, params);
    return formProps.input("Hassosaki_Address", { label: "発送先住所", maxlength: 200, is_standard, autoComplete: "on", style: { ...baseStyle, ...params, width: "30em" } }, is_debug);
  };
  /**
   * 発送先電話番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 発送先電話番号入力フィールドのJSX要素
   */
  const input_Hassosaki_Tel1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Hassosaki_Tel1 form.Hassosaki_Tel1:${form?.Hassosaki_Tel1 || ""} params:`, params);
    return formProps.input("Hassosaki_Tel1", { label: "発送先電話番号", isNumberOnly: true, maxlength: 5, is_standard, autoComplete: "on", style: { ...baseStyle, ...params, width: "9em" } }, is_debug);
  };
  const input_Hassosaki_Tel2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Hassosaki_Tel2 form.Hassosaki_Tel2:${form?.Hassosaki_Tel2 || ""} params:`, params);
    return formProps.input("Hassosaki_Tel2", { label: "発送先電話番号", isNumberOnly: true, maxlength: 5, is_standard, autoComplete: "on", style: { ...baseStyle, ...params, width: "9em" } }, is_debug);
  };
  const input_Hassosaki_Tel3 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Hassosaki_Tel3 form.Hassosaki_Tel3:${form?.Hassosaki_Tel3 || ""} params:`, params);
    return formProps.input("Hassosaki_Tel3", { label: "発送先電話番号", isNumberOnly: true, maxlength: 5, is_standard, autoComplete: "on", style: { ...baseStyle, ...params, width: "9em" } }, is_debug);
  };
  /**
   * 発送先FAX入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 発送先FAX入力フィールドのJSX要素
   */
  const input_Hassosaki_Fax = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Hassosaki_Fax form.Hassosaki_Fax:${form?.Hassosaki_Fax || ""} params:`, params);
    return formProps.input("Hassosaki_Fax", { label: "発送先FAX", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 再見積用元見積番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 再見積用元見積番号入力フィールドのJSX要素
   */
  const input_Sai_MitsumoriNo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sai_MitsumoriNo form.Sai_MitsumoriNo:${form?.Sai_MitsumoriNo || ""} params:`, params);
    return formProps.input("Sai_MitsumoriNo", { label: "再見積用元見積番号", maxlength: 12, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 再見積理由入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 再見積理由入力フィールドのJSX要素
   */
  const input_Sai_Reason = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sai_Reason form.Sai_Reason:${form?.Sai_Reason || ""} params:`, params);
    return formProps.input("Sai_Reason", { label: "再見積理由", maxlength: 200, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
    /**
   * 再見積その他内容入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 再見積その他内容入力フィールドのJSX要素
   */
    const input_SonotaText = (params = {}, is_debug = false) => {
      if (is_debug) log.debug(`input_Sai_SonotaText form.SonotaText:${form?.SonotaText || ""} params:`, params);
      return formProps.input("SonotaText", { label: "再見積理由", maxlength: 200, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
    };
  /**
   * 再見積フラグ　1:再見積済入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 再見積フラグ　1:再見積済入力フィールドのJSX要素
   */
  const input_Sai_MitsumoriFlg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sai_MitsumoriFlg form.Sai_MitsumoriFlg:${form?.Sai_MitsumoriFlg || ""} params:`, params);
    return formProps.input("Sai_MitsumoriFlg", { label: "再見積フラグ　1:再見積済", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 修理対象車輌：車台番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 修理対象車輌：車台番号入力フィールドのJSX要素
   */
  const input_Taisho_ShadaiBango = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Taisho_ShadaiBango form.Taisho_ShadaiBango:${form?.Taisho_ShadaiBango || ""} params:`, params);
    return formProps.input("Taisho_ShadaiBango", { label: "修理対象車輌：車台番号", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 修理対象車輌：メーカーCD入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 修理対象車輌：メーカーCD入力フィールドのJSX要素
   */
  const input_Taisho_MakerCD = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Taisho_MakerCD form.Taisho_MakerCD:${form?.Taisho_MakerCD || ""} params:`, params);
    return formProps.input("Taisho_MakerCD", { label: "修理対象車輌：メーカーCD", maxlength: 5, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 修理対象車輌：車名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 修理対象車輌：車名入力フィールドのJSX要素
   */
  const input_Taisho_CarName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Taisho_CarName form.Taisho_CarName:${form?.Taisho_CarName || ""} params:`, params);
    return formProps.input("Taisho_CarName", { label: "修理対象車輌：車名", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 修理対象車輌：型式指定入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 修理対象車輌：型式指定入力フィールドのJSX要素
   */
  const input_Taisho_Katashiki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Taisho_Katashiki form.Taisho_Katashiki:${form?.Taisho_Katashiki || ""} params:`, params);
    return formProps.input("Taisho_Katashiki", { label: "修理対象車輌：型式指定", maxlength: 10, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 修理対象車輌：類別番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 修理対象車輌：類別番号入力フィールドのJSX要素
   */
  const input_Taisho_Ruibetsu = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Taisho_Ruibetsu form.Taisho_Ruibetsu:${form?.Taisho_Ruibetsu || ""} params:`, params);
    return formProps.input("Taisho_Ruibetsu", { label: "修理対象車輌：類別番号", maxlength: 10, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 修理対象車輌：車輌型式入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 修理対象車輌：車輌型式入力フィールドのJSX要素
   */
  const input_Taisho_SharyoKatashiki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Taisho_SharyoKatashiki form.Taisho_SharyoKatashiki:${form?.Taisho_SharyoKatashiki || ""} params:`, params);
    return formProps.input("Taisho_SharyoKatashiki", { label: "修理対象車輌：車輌型式", maxlength: 30, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 入力経過フラグ　3:依頼情報入力画面 A-2-3　4:部位選択画面 A-2-5　5:エリア選択画面 A-2-6　6:見積期限画面 A-2-7　7:写真添付画面 A-2-8入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 入力経過フラグ　3:依頼情報入力画面 A-2-3　4:部位選択画面 A-2-5　5:エリア選択画面 A-2-6　6:見積期限画面 A-2-7　7:写真添付画面 A-2-8入力フィールドのJSX要素
   */
  const input_Input_Status = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Input_Status form.Input_Status:${form?.Input_Status || ""} params:`, params);
    return formProps.input(
      "Input_Status",
      {
        label: "入力経過フラグ　3:依頼情報入力画面 A-2-3　4:部位選択画面 A-2-5　5:エリア選択画面 A-2-6　6:見積期限画面 A-2-7　7:写真添付画面 A-2-8",
        isNumberOnly: true,
        isMinus: true,
        is_standard, autoComplete: "on",
        style: baseStyle,
        ...params,
      },
      is_debug
    );
  };
  /**
   * オークション出品番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} オークション出品番号入力フィールドのJSX要素
   */
  const input_AuctionNo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_AuctionNo form.AuctionNo:${form?.AuctionNo || ""} params:`, params);
    return formProps.input("AuctionNo", { label: "オークション出品番号", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * オークションお断り金額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} オークションお断り金額入力フィールドのJSX要素
   */
  const input_DummyKingaku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_DummyKingaku form.DummyKingaku:${form?.DummyKingaku || ""} params:`, params);
    return formProps.input("DummyKingaku", { label: "オークションお断り金額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * オークション依頼者入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} オークション依頼者入力フィールドのJSX要素
   */
  const input_AuctionIraisha = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_AuctionIraisha form.AuctionIraisha:${form?.AuctionIraisha || ""} params:`, params);
    return formProps.input("AuctionIraisha", { label: "オークション依頼者", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * DIC新一等書記官 見積番号1入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} DIC新一等書記官 見積番号1入力フィールドのJSX要素
   */
  const input_DIC_ShokikanNo_F = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_DIC_ShokikanNo_F form.DIC_ShokikanNo_F:${form?.DIC_ShokikanNo_F || ""} params:`, params);
    return formProps.input("DIC_ShokikanNo_F", { label: "DIC新一等書記官 見積番号1", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * DIC新一等書記官 見積番号2入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} DIC新一等書記官 見積番号2入力フィールドのJSX要素
   */
  const input_DIC_ShokikanNo_S = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_DIC_ShokikanNo_S form.DIC_ShokikanNo_S:${form?.DIC_ShokikanNo_S || ""} params:`, params);
    return formProps.input("DIC_ShokikanNo_S", { label: "DIC新一等書記官 見積番号2", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * DIC新一等書記官 見積番号3入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} DIC新一等書記官 見積番号3入力フィールドのJSX要素
   */
  const input_DIC_ShokikanNo_T = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_DIC_ShokikanNo_T form.DIC_ShokikanNo_T:${form?.DIC_ShokikanNo_T || ""} params:`, params);
    return formProps.input("DIC_ShokikanNo_T", { label: "DIC新一等書記官 見積番号3", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 住所入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 住所入力フィールドのJSX要素
   */
  const input_Jyusho = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Jyusho form.Jyusho:${form?.Jyusho || ""} params:`, params);
    return formProps.input("Jyusho", { label: "住所", maxlength: 200, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌種別ID入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌種別ID入力フィールドのJSX要素
   */
  const input_CarKindID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CarKindID form.CarKindID:${form?.CarKindID || ""} params:`, params);
    return formProps.input("CarKindID", { label: "車輌種別ID", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌種別名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌種別名入力フィールドのJSX要素
   */
  const input_CarKindName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CarKindName form.CarKindName:${form?.CarKindName || ""} params:`, params);
    return formProps.input("CarKindName", { label: "車輌種別名", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * パーツ種別名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} パーツ種別名入力フィールドのJSX要素
   */
  const input_PartsKindName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_PartsKindName form.PartsKindName:${form?.PartsKindName || ""} params:`, params);
    return formProps.input("PartsKindName", { label: "パーツ種別名", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 管理ID入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 管理ID入力フィールドのJSX要素
   */
  const input_KanriID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_KanriID form.KanriID:${form?.KanriID || ""} params:`, params);
    return formProps.input("KanriID", { label: "管理ID", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * FAX(予約)入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} FAX(予約)入力フィールドのJSX要素
   */
  const input_FAX = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_FAX form.FAX:${form?.FAX || ""} params:`, params);
    return formProps.input("FAX", { label: "FAX(予約)", maxlength: 14, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 定休フラグ 1:定休日 2:臨時入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 定休フラグ 1:定休日 2:臨時入力フィールドのJSX要素
   */
  const input_CloseFlg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CloseFlg form.CloseFlg:${form?.CloseFlg || ""} params:`, params);
    return formProps.input("CloseFlg", { label: "定休フラグ 1:定休日 2:臨時", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 定休日入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 定休日入力フィールドのJSX要素
   */
  const input_CloseDate = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CloseDate form.CloseDate:${form?.CloseDate || ""} params:`, params);
    return formProps.input("CloseDate", { label: "定休日", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 休みフラグ　1:全休 2:AM休 3:PM休入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 休みフラグ　1:全休 2:AM休 3:PM休入力フィールドのJSX要素
   */
  const input_HolidayFlg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_HolidayFlg form.HolidayFlg:${form?.HolidayFlg || ""} params:`, params);
    return formProps.input("HolidayFlg", { label: "休みフラグ　1:全休 2:AM休 3:PM休", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * コメント入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} コメント入力フィールドのJSX要素
   */
  const input_CloseMemo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CloseMemo form.CloseMemo:${form?.CloseMemo || ""} params:`, params);
    return formProps.input("CloseMemo", { label: "コメント", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 表題入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 表題入力フィールドのJSX要素
   */
  const input_Title = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Title form.Title:${form?.Title || ""} params:`, params);
    return formProps.input("Title", { label: "表題", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 内容入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 内容入力フィールドのJSX要素
   */
  const input_Naiyo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Naiyo form.Naiyo:${form?.Naiyo || ""} params:`, params);
    return formProps.input("Naiyo", { label: "内容", maxlength: 2000, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 画像ファイル入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 画像ファイル入力フィールドのJSX要素
   */
  const input_Gazo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Gazo form.Gazo:${form?.Gazo || ""} params:`, params);
    return formProps.input("Gazo", { label: "画像ファイル", maxlength: 200, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 種別 0:全体 1:グループ 2:業種入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 種別 0:全体 1:グループ 2:業種入力フィールドのJSX要素
   */
  const input_RegKind = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_RegKind form.RegKind:${form?.RegKind || ""} params:`, params);
    return formProps.input("RegKind", { label: "種別 0:全体 1:グループ 2:業種", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * ID入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} ID入力フィールドのJSX要素
   */
  const input_ID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ID form.ID:${form?.ID || ""} params:`, params);
    return formProps.input("ID", { label: "ID", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * カラーコード入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} カラーコード入力フィールドのJSX要素
   */
  const input_ColorCD = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ColorCD form.ColorCD:${form?.ColorCD || ""} params:`, params);
    return formProps.input("ColorCD", { label: "カラーコード", maxlength: 10, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 2トーンフラグ　0=シングル 1=多色入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 2トーンフラグ　0=シングル 1=多色入力フィールドのJSX要素
   */
  const input_Color2Tone = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Color2Tone form.Color2Tone:${form?.Color2Tone || ""} params:`, params);
    return formProps.input("Color2Tone", { label: "2トーンフラグ　0=シングル 1=多色", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 補修対応入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 補修対応入力フィールドのJSX要素
   */
  const input_Hoshuu = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Hoshuu form.Hoshuu:${form?.Hoshuu || ""} params:`, params);
    return formProps.input("Hoshuu", { label: "補修対応", maxlength: 10, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車種コード入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車種コード入力フィールドのJSX要素
   */
  const input_CarCD = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CarCD form.CarCD:${form?.CarCD || ""} params:`, params);
    return formProps.input("CarCD", { label: "車種コード", maxlength: 2, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車種サブコード入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車種サブコード入力フィールドのJSX要素
   */
  const input_CarSubCD = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CarSubCD form.CarSubCD:${form?.CarSubCD || ""} params:`, params);
    return formProps.input("CarSubCD", { label: "車種サブコード", maxlength: 2, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌型式入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌型式入力フィールドのJSX要素
   */
  const input_SharyoKatashiki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_SharyoKatashiki form.SharyoKatashiki:${form?.SharyoKatashiki || ""} params:`, params);
    return formProps.input("SharyoKatashiki", { label: "車輌型式", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌フル型式入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌フル型式入力フィールドのJSX要素
   */
  const input_SharyoFullKatashiki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_SharyoFullKatashiki form.SharyoFullKatashiki:${form?.SharyoFullKatashiki || ""} params:`, params);
    return formProps.input("SharyoFullKatashiki", { label: "車輌フル型式", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車台番号開始入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車台番号開始入力フィールドのJSX要素
   */
  const input_ShadaiBango_From = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ShadaiBango_From form.ShadaiBango_From:${form?.ShadaiBango_From || ""} params:`, params);
    return formProps.input("ShadaiBango_From", { label: "車台番号開始", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車台番号終了入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車台番号終了入力フィールドのJSX要素
   */
  const input_ShadaiBango_To = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ShadaiBango_To form.ShadaiBango_To:${form?.ShadaiBango_To || ""} params:`, params);
    return formProps.input("ShadaiBango_To", { label: "車台番号終了", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 自動車種別入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 自動車種別入力フィールドのJSX要素
   */
  const input_Sharyo_Shubetsu = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Shubetsu form.Sharyo_Shubetsu:${form?.Sharyo_Shubetsu || ""} params:`, params);
    return formProps.input("Sharyo_Shubetsu", { label: "自動車種別", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車体形状入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車体形状入力フィールドのJSX要素
   */
  const input_Shatai_Keijyo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shatai_Keijyo form.Shatai_Keijyo:${form?.Shatai_Keijyo || ""} params:`, params);
    return formProps.input("Shatai_Keijyo", { label: "車体形状", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌形状(ドア数など)入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌形状(ドア数など)入力フィールドのJSX要素
   */
  const input_Sharyo_Keijyo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Keijyo form.Sharyo_Keijyo:${form?.Sharyo_Keijyo || ""} params:`, params);
    return formProps.input("Sharyo_Keijyo", { label: "車輌形状(ドア数など)", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌用途入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌用途入力フィールドのJSX要素
   */
  const input_Sharyo_Youto = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Youto form.Sharyo_Youto:${form?.Sharyo_Youto || ""} params:`, params);
    return formProps.input("Sharyo_Youto", { label: "車輌用途", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 2種ある場合のエンジン型式入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 2種ある場合のエンジン型式入力フィールドのJSX要素
   */
  const input_Sharyo_EngineKatashiki2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_EngineKatashiki2 form.Sharyo_EngineKatashiki2:${form?.Sharyo_EngineKatashiki2 || ""} params:`, params);
    return formProps.input("Sharyo_EngineKatashiki2", { label: "2種ある場合のエンジン型式", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 年式(始期)入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 年式(始期)入力フィールドのJSX要素
   */
  const input_Sharyo_Nenshiki_st = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Nenshiki_st form.Sharyo_Nenshiki_st:${form?.Sharyo_Nenshiki_st || ""} params:`, params);
    return formProps.input("Sharyo_Nenshiki_st", { label: "年式(始期)", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 年式(終期)入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 年式(終期)入力フィールドのJSX要素
   */
  const input_Sharyo_Nenshiki_ed = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Nenshiki_ed form.Sharyo_Nenshiki_ed:${form?.Sharyo_Nenshiki_ed || ""} params:`, params);
    return formProps.input("Sharyo_Nenshiki_ed", { label: "年式(終期)", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 期別入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 期別入力フィールドのJSX要素
   */
  const input_Sharyo_Kibetsu = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Kibetsu form.Sharyo_Kibetsu:${form?.Sharyo_Kibetsu || ""} params:`, params);
    return formProps.input("Sharyo_Kibetsu", { label: "期別", maxlength: 10, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 外装入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 外装入力フィールドのJSX要素
   */
  const input_Shiyo_Gaiso = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shiyo_Gaiso form.Shiyo_Gaiso:${form?.Shiyo_Gaiso || ""} params:`, params);
    return formProps.input("Shiyo_Gaiso", { label: "外装", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * エンジン入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} エンジン入力フィールドのJSX要素
   */
  const input_Shiyo_Engine = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shiyo_Engine form.Shiyo_Engine:${form?.Shiyo_Engine || ""} params:`, params);
    return formProps.input("Shiyo_Engine", { label: "エンジン", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 走行入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 走行入力フィールドのJSX要素
   */
  const input_Shiyo_Soukou = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shiyo_Soukou form.Shiyo_Soukou:${form?.Shiyo_Soukou || ""} params:`, params);
    return formProps.input("Shiyo_Soukou", { label: "走行", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * トランスNo入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} トランスNo入力フィールドのJSX要素
   */
  const input_Shiyo_TransNo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shiyo_TransNo form.Shiyo_TransNo:${form?.Shiyo_TransNo || ""} params:`, params);
    return formProps.input("Shiyo_TransNo", { label: "トランスNo", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 特殊車輌入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 特殊車輌入力フィールドのJSX要素
   */
  const input_Shiyo_Tokushu = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shiyo_Tokushu form.Shiyo_Tokushu:${form?.Shiyo_Tokushu || ""} params:`, params);
    return formProps.input("Shiyo_Tokushu", { label: "特殊車輌", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * エンジンNo入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} エンジンNo入力フィールドのJSX要素
   */
  const input_Shiyo_EngineNo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shiyo_EngineNo form.Shiyo_EngineNo:${form?.Shiyo_EngineNo || ""} params:`, params);
    return formProps.input("Shiyo_EngineNo", { label: "エンジンNo", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * アクスル/ギア比入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} アクスル/ギア比入力フィールドのJSX要素
   */
  const input_Shiyo_GearRatio = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shiyo_GearRatio form.Shiyo_GearRatio:${form?.Shiyo_GearRatio || ""} params:`, params);
    return formProps.input("Shiyo_GearRatio", { label: "アクスル/ギア比", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 乗車定員１入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 乗車定員１入力フィールドのJSX要素
   */
  const input_Jyosha1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Jyosha1 form.Jyosha1:${form?.Jyosha1 || ""} params:`, params);
    return formProps.input("Jyosha1", { label: "乗車定員１", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 乗車定員２入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 乗車定員２入力フィールドのJSX要素
   */
  const input_Jyosha2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Jyosha2 form.Jyosha2:${form?.Jyosha2 || ""} params:`, params);
    return formProps.input("Jyosha2", { label: "乗車定員２", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 最大積載量１入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 最大積載量１入力フィールドのJSX要素
   */
  const input_Sekisai1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sekisai1 form.Sekisai1:${form?.Sekisai1 || ""} params:`, params);
    return formProps.input("Sekisai1", { label: "最大積載量１", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 最大積載量２入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 最大積載量２入力フィールドのJSX要素
   */
  const input_Sekisai2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sekisai2 form.Sekisai2:${form?.Sekisai2 || ""} params:`, params);
    return formProps.input("Sekisai2", { label: "最大積載量２", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌重量１入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌重量１入力フィールドのJSX要素
   */
  const input_Jyuryo1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Jyuryo1 form.Jyuryo1:${form?.Jyuryo1 || ""} params:`, params);
    return formProps.input("Jyuryo1", { label: "車輌重量１", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌重量２入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌重量２入力フィールドのJSX要素
   */
  const input_Jyuryo2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Jyuryo2 form.Jyuryo2:${form?.Jyuryo2 || ""} params:`, params);
    return formProps.input("Jyuryo2", { label: "車輌重量２", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌総重量１入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌総重量１入力フィールドのJSX要素
   */
  const input_Sojyuryo1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sojyuryo1 form.Sojyuryo1:${form?.Sojyuryo1 || ""} params:`, params);
    return formProps.input("Sojyuryo1", { label: "車輌総重量１", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌総重量２入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌総重量２入力フィールドのJSX要素
   */
  const input_Sojyuryo2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sojyuryo2 form.Sojyuryo2:${form?.Sojyuryo2 || ""} params:`, params);
    return formProps.input("Sojyuryo2", { label: "車輌総重量２", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 長さ入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 長さ入力フィールドのJSX要素
   */
  const input_Length = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Length form.Length:${form?.Length || ""} params:`, params);
    return formProps.input("Length", { label: "長さ", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 幅入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 幅入力フィールドのJSX要素
   */
  const input_Width = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Width form.Width:${form?.Width || ""} params:`, params);
    return formProps.input("Width", { label: "幅", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 高さ入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 高さ入力フィールドのJSX要素
   */
  const input_Height = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Height form.Height:${form?.Height || ""} params:`, params);
    return formProps.input("Height", { label: "高さ", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 前前軸重入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 前前軸重入力フィールドのJSX要素
   */
  const input_MMJiku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_MMJiku form.MMJiku:${form?.MMJiku || ""} params:`, params);
    return formProps.input("MMJiku", { label: "前前軸重", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 前後軸重入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 前後軸重入力フィールドのJSX要素
   */
  const input_MGJiku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_MGJiku form.MGJiku:${form?.MGJiku || ""} params:`, params);
    return formProps.input("MGJiku", { label: "前後軸重", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 後前軸重入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 後前軸重入力フィールドのJSX要素
   */
  const input_GMJiku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_GMJiku form.GMJiku:${form?.GMJiku || ""} params:`, params);
    return formProps.input("GMJiku", { label: "後前軸重", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 後後軸重入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 後後軸重入力フィールドのJSX要素
   */
  const input_GGJiku = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_GGJiku form.GGJiku:${form?.GGJiku || ""} params:`, params);
    return formProps.input("GGJiku", { label: "後後軸重", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * フロント/タイヤサイズ入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} フロント/タイヤサイズ入力フィールドのJSX要素
   */
  const input_F_tiresize = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_F_tiresize form.F_tiresize:${form?.F_tiresize || ""} params:`, params);
    return formProps.input("F_tiresize", { label: "フロント/タイヤサイズ", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * リヤ/タイヤサイズ入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} リヤ/タイヤサイズ入力フィールドのJSX要素
   */
  const input_R_tiresize = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_R_tiresize form.R_tiresize:${form?.R_tiresize || ""} params:`, params);
    return formProps.input("R_tiresize", { label: "リヤ/タイヤサイズ", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * フロント/ホイールサイズ入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} フロント/ホイールサイズ入力フィールドのJSX要素
   */
  const input_F_Wheelsize = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_F_Wheelsize form.F_Wheelsize:${form?.F_Wheelsize || ""} params:`, params);
    return formProps.input("F_Wheelsize", { label: "フロント/ホイールサイズ", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * フロント/ホイールオフセット入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} フロント/ホイールオフセット入力フィールドのJSX要素
   */
  const input_F_Wheeloffset = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_F_Wheeloffset form.F_Wheeloffset:${form?.F_Wheeloffset || ""} params:`, params);
    return formProps.input("F_Wheeloffset", { label: "フロント/ホイールオフセット", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * リヤ/ホイールサイズ入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} リヤ/ホイールサイズ入力フィールドのJSX要素
   */
  const input_R_Wheelsize = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_R_Wheelsize form.R_Wheelsize:${form?.R_Wheelsize || ""} params:`, params);
    return formProps.input("R_Wheelsize", { label: "リヤ/ホイールサイズ", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * リヤ/ホイールオフセット入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} リヤ/ホイールオフセット入力フィールドのJSX要素
   */
  const input_R_Wheeloffset = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_R_Wheeloffset form.R_Wheeloffset:${form?.R_Wheeloffset || ""} params:`, params);
    return formProps.input("R_Wheeloffset", { label: "リヤ/ホイールオフセット", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * バッテリーサイズ入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} バッテリーサイズ入力フィールドのJSX要素
   */
  const input_BatterySize = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_BatterySize form.BatterySize:${form?.BatterySize || ""} params:`, params);
    return formProps.input("BatterySize", { label: "バッテリーサイズ", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * ハンドル位置入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} ハンドル位置入力フィールドのJSX要素
   */
  const input_Steering = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Steering form.Steering:${form?.Steering || ""} params:`, params);
    return formProps.input("Steering", { label: "ハンドル位置", maxlength: 4, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 地域仕様入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 地域仕様入力フィールドのJSX要素
   */
  const input_District = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_District form.District:${form?.District || ""} params:`, params);
    return formProps.input("District", { label: "地域仕様", maxlength: 10, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌備考入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌備考入力フィールドのJSX要素
   */
  const input_Memo_Bikou = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Memo_Bikou form.Memo_Bikou:${form?.Memo_Bikou || ""} params:`, params);
    return formProps.input("Memo_Bikou", { label: "車輌備考", maxlength: 200, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * フロン 1:新フロン 2:旧フロン 3:なし入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} フロン 1:新フロン 2:旧フロン 3:なし入力フィールドのJSX要素
   */
  const input_Freon = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Freon form.Freon:${form?.Freon || ""} params:`, params);
    return formProps.input("Freon", { label: "フロン 1:新フロン 2:旧フロン 3:なし", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * エアバッグ入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} エアバッグ入力フィールドのJSX要素
   */
  const input_AirBag = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_AirBag form.AirBag:${form?.AirBag || ""} params:`, params);
    return formProps.input("AirBag", { label: "エアバッグ", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * Zwesシステムの照会Flag 1:照会済入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} Zwesシステムの照会Flag 1:照会済入力フィールドのJSX要素
   */
  const input_Zwes_Flag = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Zwes_Flag form.Zwes_Flag:${form?.Zwes_Flag || ""} params:`, params);
    return formProps.input("Zwes_Flag", { label: "Zwesシステムの照会Flag 1:照会済", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 分類コード入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 分類コード入力フィールドのJSX要素
   */
  const input_BunruiCD = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_BunruiCD form.BunruiCD:${form?.BunruiCD || ""} params:`, params);
    return formProps.input("BunruiCD", { label: "分類コード", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 上位分類コード入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 上位分類コード入力フィールドのJSX要素
   */
  const input_BunruiCD_oya = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_BunruiCD_oya form.BunruiCD_oya:${form?.BunruiCD_oya || ""} params:`, params);
    return formProps.input("BunruiCD_oya", { label: "上位分類コード", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 親4CD入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 親4CD入力フィールドのJSX要素
   */
  const input_Base4CD = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Base4CD form.Base4CD:${form?.Base4CD || ""} params:`, params);
    return formProps.input("Base4CD", { label: "親4CD", maxlength: 5, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 分類名称 バンパーなど入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 分類名称 バンパーなど入力フィールドのJSX要素
   */
  const input_BunruiName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_BunruiName form.BunruiName:${form?.BunruiName || ""} params:`, params);
    return formProps.input("BunruiName", { label: "分類名称 バンパーなど", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 経理用メールアドレス入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 経理用メールアドレス入力フィールドのJSX要素
   */
  const input_Email_Keiri = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Email_Keiri form.Email_Keiri:${form?.Email_Keiri || ""} params:`, params);
    return formProps.input("Email_Keiri", { label: "経理用メールアドレス", maxlength: 200, isMailAddress: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 企業名カナ入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 企業名カナ入力フィールドのJSX要素
   */
  const input_CorpKana = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CorpKana form.CorpKana:${form?.CorpKana || ""} params:`, params);
    return formProps.input("CorpKana", { label: "企業名カナ", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 代表者名(姓)入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 代表者名(姓)入力フィールドのJSX要素
   */
  const input_DaihyoName1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_DaihyoName1 form.DaihyoName1:${form?.DaihyoName1 || ""} params:`, params);
    return formProps.input("DaihyoName1", { label: "代表者名(姓)", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 代表者名(名)入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 代表者名(名)入力フィールドのJSX要素
   */
  const input_DaihyoName2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_DaihyoName2 form.DaihyoName2:${form?.DaihyoName2 || ""} params:`, params);
    return formProps.input("DaihyoName2", { label: "代表者名(名)", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 代表者カナ(姓)入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 代表者カナ(姓)入力フィールドのJSX要素
   */
  const input_DaihyoKana1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_DaihyoKana1 form.DaihyoKana1:${form?.DaihyoKana1 || ""} params:`, params);
    return formProps.input("DaihyoKana1", { label: "代表者カナ(姓)", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 代表者カナ(名)入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 代表者カナ(名)入力フィールドのJSX要素
   */
  const input_DaihyoKana2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_DaihyoKana2 form.DaihyoKana2:${form?.DaihyoKana2 || ""} params:`, params);
    return formProps.input("DaihyoKana2", { label: "代表者カナ(名)", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * asaasシステム番号、京都商工認証番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} asaasシステム番号、京都商工認証番号入力フィールドのJSX要素
   */
  const input_JyuchuNoFormat = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_JyuchuNoFormat form.JyuchuNoFormat:${form?.JyuchuNoFormat || ""} params:`, params);
    return formProps.input("JyuchuNoFormat", { label: "asaasシステム番号、京都商工認証番号", maxlength: 40, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 受注番号区分入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 受注番号区分入力フィールドのJSX要素
   */
  const input_JyuchuNoKuwake = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_JyuchuNoKuwake form.JyuchuNoKuwake:${form?.JyuchuNoKuwake || ""} params:`, params);
    return formProps.input("JyuchuNoKuwake", { label: "受注番号区分", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 法人番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 法人番号入力フィールドのJSX要素
   */
  const input_KarteNoFormat = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_KarteNoFormat form.KarteNoFormat:${form?.KarteNoFormat || ""} params:`, params);
    return formProps.input("KarteNoFormat", { label: "法人番号", maxlength: 40, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 自社管理番号区分　1=年単位 2=月単位 9=通し入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 自社管理番号区分　1=年単位 2=月単位 9=通し入力フィールドのJSX要素
   */
  const input_KarteNoKuwake = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_KarteNoKuwake form.KarteNoKuwake:${form?.KarteNoKuwake || ""} params:`, params);
    return formProps.input("KarteNoKuwake", { label: "自社管理番号区分　1=年単位 2=月単位 9=通し", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 送料選択区分　1:地域別　2:都道府県別入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 送料選択区分　1:地域別　2:都道府県別入力フィールドのJSX要素
   */
  const input_DelivKind = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_DelivKind form.DelivKind:${form?.DelivKind || ""} params:`, params);
    return formProps.input("DelivKind", { label: "送料選択区分　1:地域別　2:都道府県別", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 検索フラグ 2=検索メニュー2のみ表示入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 検索フラグ 2=検索メニュー2のみ表示入力フィールドのJSX要素
   */
  const input_Search_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Search_flg form.Search_flg:${form?.Search_flg || ""} params:`, params);
    return formProps.input("Search_flg", { label: "検索フラグ 2=検索メニュー2のみ表示", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * ファクタリング上限金額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} ファクタリング上限金額入力フィールドのJSX要素
   */
  const input_Factoring = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Factoring form.Factoring:${form?.Factoring || ""} params:`, params);
    return formProps.input("Factoring", { label: "ファクタリング上限金額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 保証金入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 保証金入力フィールドのJSX要素
   */
  const input_Hoshokin = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Hoshokin form.Hoshokin:${form?.Hoshokin || ""} params:`, params);
    return formProps.input("Hoshokin", { label: "保証金", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 業種フラグ入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 業種フラグ入力フィールドのJSX要素
   */
  const input_Gyoshu_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Gyoshu_flg form.Gyoshu_flg:${form?.Gyoshu_flg || ""} params:`, params);
    return formProps.input("Gyoshu_flg", { label: "業種フラグ", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * BS会員コード入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} BS会員コード入力フィールドのJSX要素
   */
  const input_BSCode = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_BSCode form.BSCode:${form?.BSCode || ""} params:`, params);
    return formProps.input("BSCode", { label: "BS会員コード", maxlength: 10, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * BS用セディナコード入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} BS用セディナコード入力フィールドのJSX要素
   */
  const input_Sedina = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sedina form.Sedina:${form?.Sedina || ""} params:`, params);
    return formProps.input("Sedina", { label: "BS用セディナコード", maxlength: 10, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * Movi弥生売掛補助科目入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} Movi弥生売掛補助科目入力フィールドのJSX要素
   */
  const input_Yayoi_UriNo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Yayoi_UriNo form.Yayoi_UriNo:${form?.Yayoi_UriNo || ""} params:`, params);
    return formProps.input("Yayoi_UriNo", { label: "Movi弥生売掛補助科目", maxlength: 30, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * Movi弥生買掛補助科目入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} Movi弥生買掛補助科目入力フィールドのJSX要素
   */
  const input_Yayoi_KaiNo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Yayoi_KaiNo form.Yayoi_KaiNo:${form?.Yayoi_KaiNo || ""} params:`, params);
    return formProps.input("Yayoi_KaiNo", { label: "Movi弥生買掛補助科目", maxlength: 30, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 企業名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 企業名入力フィールドのJSX要素
   */
  const input_Oya_CorpID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Oya_CorpID form.Oya_CorpID:${form?.Oya_CorpID || ""} params:`, params);
    return formProps.input("Oya_CorpID", { label: "企業名", maxlength: 12, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 入力フィールドのJSX要素
   */
  const input_Sname = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sname form.Sname:${form?.Sname || ""} params:`, params);
    return formProps.input("Sname", { label: "", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 並び順入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 並び順入力フィールドのJSX要素
   */
  const input_seq = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_seq form.seq:${form?.seq || ""} params:`, params);
    return formProps.input("seq", { label: "並び順", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * PHM_NumberPlatesのID入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} PHM_NumberPlatesのID入力フィールドのJSX要素
   */
  const input_PNPid = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_PNPid form.PNPid:${form?.PNPid || ""} params:`, params);
    return formProps.input("PNPid", { label: "PHM_NumberPlatesのID", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 地名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 地名入力フィールドのJSX要素
   */
  const input_Chimei = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Chimei form.Chimei:${form?.Chimei || ""} params:`, params);
    return formProps.input("Chimei", { label: "地名", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 都道府県名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 都道府県名入力フィールドのJSX要素
   */
  const input_Todo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Todo form.Todo:${form?.Todo || ""} params:`, params);
    return formProps.input("Todo", { label: "都道府県名", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 区域名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 区域名入力フィールドのJSX要素
   */
  const input_Kuiki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Kuiki form.Kuiki:${form?.Kuiki || ""} params:`, params);
    return formProps.input("Kuiki", { label: "区域名", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 表示順入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 表示順入力フィールドのJSX要素
   */
  const input_Hyouji = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Hyouji form.Hyouji:${form?.Hyouji || ""} params:`, params);
    return formProps.input("Hyouji", { label: "表示順", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * Zwse照会用2入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} Zwse照会用2入力フィールドのJSX要素
   */
  const input_Zwse4CD2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Zwse4CD2 form.Zwse4CD2:${form?.Zwse4CD2 || ""} params:`, params);
    return formProps.input("Zwse4CD2", { label: "Zwse照会用2", maxlength: 5, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * Zwse照会用3入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} Zwse照会用3入力フィールドのJSX要素
   */
  const input_Zwse4CD3 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Zwse4CD3 form.Zwse4CD3:${form?.Zwse4CD3 || ""} params:`, params);
    return formProps.input("Zwse4CD3", { label: "Zwse照会用3", maxlength: 5, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * Zwse照会用4入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} Zwse照会用4入力フィールドのJSX要素
   */
  const input_Zwse4CD4 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Zwse4CD4 form.Zwse4CD4:${form?.Zwse4CD4 || ""} params:`, params);
    return formProps.input("Zwse4CD4", { label: "Zwse照会用4", maxlength: 5, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * Zwse照会用5入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} Zwse照会用5入力フィールドのJSX要素
   */
  const input_Zwse4CD5 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Zwse4CD5 form.Zwse4CD5:${form?.Zwse4CD5 || ""} params:`, params);
    return formProps.input("Zwse4CD5", { label: "Zwse照会用5", maxlength: 5, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * Ayフラグ 1=Ay親 2=Ay子 3=単品入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} Ayフラグ 1=Ay親 2=Ay子 3=単品入力フィールドのJSX要素
   */
  const input_AyFlg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_AyFlg form.AyFlg:${form?.AyFlg || ""} params:`, params);
    return formProps.input("AyFlg", { label: "Ayフラグ 1=Ay親 2=Ay子 3=単品", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * CO2集計 1=対象入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} CO2集計 1=対象入力フィールドのJSX要素
   */
  const input_co2_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_co2_flg form.co2_flg:${form?.co2_flg || ""} params:`, params);
    return formProps.input("co2_flg", { label: "CO2集計 1=対象", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * CO2排出削減値入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} CO2排出削減値入力フィールドのJSX要素
   */
  const input_co2_num = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_co2_num form.co2_num:${form?.co2_num || ""} params:`, params);
    return formProps.input("co2_num", { label: "CO2排出削減値", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * CO2集計リビルト 1=対象入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} CO2集計リビルト 1=対象入力フィールドのJSX要素
   */
  const input_co2_flg_r = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_co2_flg_r form.co2_flg_r:${form?.co2_flg_r || ""} params:`, params);
    return formProps.input("co2_flg_r", { label: "CO2集計リビルト 1=対象", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * CO2排出削減値リビルト入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} CO2排出削減値リビルト入力フィールドのJSX要素
   */
  const input_co2_num_r = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_co2_num_r form.co2_num_r:${form?.co2_num_r || ""} params:`, params);
    return formProps.input("co2_num_r", { label: "CO2排出削減値リビルト", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 消費税率入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 消費税率入力フィールドのJSX要素
   */
  const input_Zeiritsu = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Zeiritsu form.Zeiritsu:${form?.Zeiritsu || ""} params:`, params);
    return formProps.input("Zeiritsu", { label: "消費税率", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 市区町村名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 市区町村名入力フィールドのJSX要素
   */
  const input_Shikuchoson = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shikuchoson form.Shikuchoson:${form?.Shikuchoson || ""} params:`, params);
    return formProps.input("Shikuchoson", { label: "市区町村名", maxlength: 80, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 町域名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 町域名入力フィールドのJSX要素
   */
  const input_Chouiki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Chouiki form.Chouiki:${form?.Chouiki || ""} params:`, params);
    return formProps.input("Chouiki", { label: "町域名", maxlength: 80, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車台番号入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車台番号入力フィールドのJSX要素
   */
  const input_ShadaiBango = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ShadaiBango form.ShadaiBango:${form?.ShadaiBango || ""} params:`, params);
    return formProps.input("ShadaiBango", { label: "車台番号", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * ミッション型式入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} ミッション型式入力フィールドのJSX要素
   */
  const input_MissionKatashiki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_MissionKatashiki form.MissionKatashiki:${form?.MissionKatashiki || ""} params:`, params);
    return formProps.input("MissionKatashiki", { label: "ミッション型式", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * MT数入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} MT数入力フィールドのJSX要素
   */
  const input_MTNum = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_MTNum form.MTNum:${form?.MTNum || ""} params:`, params);
    return formProps.input("MTNum", { label: "MT数", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 馬力入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 馬力入力フィールドのJSX要素
   */
  const input_Bariki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Bariki form.Bariki:${form?.Bariki || ""} params:`, params);
    return formProps.input("Bariki", { label: "馬力", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * ボディ形状入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} ボディ形状入力フィールドのJSX要素
   */
  const input_BodyKeijyo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_BodyKeijyo form.BodyKeijyo:${form?.BodyKeijyo || ""} params:`, params);
    return formProps.input("BodyKeijyo", { label: "ボディ形状", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * キャビン形状入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} キャビン形状入力フィールドのJSX要素
   */
  const input_CabinKeijyo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CabinKeijyo form.CabinKeijyo:${form?.CabinKeijyo || ""} params:`, params);
    return formProps.input("CabinKeijyo", { label: "キャビン形状", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 電圧入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 電圧入力フィールドのJSX要素
   */
  const input_Denatsu = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Denatsu form.Denatsu:${form?.Denatsu || ""} params:`, params);
    return formProps.input("Denatsu", { label: "電圧", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌保管場所入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌保管場所入力フィールドのJSX要素
   */
  const input_HokanBasho = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_HokanBasho form.HokanBasho:${form?.HokanBasho || ""} params:`, params);
    return formProps.input("HokanBasho", { label: "車輌保管場所", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 仕入担当者入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 仕入担当者入力フィールドのJSX要素
   */
  const input_PurchaTanto = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_PurchaTanto form.PurchaTanto:${form?.PurchaTanto || ""} params:`, params);
    return formProps.input("PurchaTanto", { label: "仕入担当者", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 仕入項目入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 仕入項目入力フィールドのJSX要素
   */
  const input_PurchaItem = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_PurchaItem form.PurchaItem:${form?.PurchaItem || ""} params:`, params);
    return formProps.input("PurchaItem", { label: "仕入項目", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 仕入先入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 仕入先入力フィールドのJSX要素
   */
  const input_PurchaCorp = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_PurchaCorp form.PurchaCorp:${form?.PurchaCorp || ""} params:`, params);
    return formProps.input("PurchaCorp", { label: "仕入先", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 仕入金額入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 仕入金額入力フィールドのJSX要素
   */
  const input_PurchaPrice = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_PurchaPrice form.PurchaPrice:${form?.PurchaPrice || ""} params:`, params);
    return formProps.input("PurchaPrice", { label: "仕入金額", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 陸送外注先名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 陸送外注先名入力フィールドのJSX要素
   */
  const input_RikusouTanto = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_RikusouTanto form.RikusouTanto:${form?.RikusouTanto || ""} params:`, params);
    return formProps.input("RikusouTanto", { label: "陸送外注先名", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 陸送料金入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 陸送料金入力フィールドのJSX要素
   */
  const input_RikusouPrice = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_RikusouPrice form.RikusouPrice:${form?.RikusouPrice || ""} params:`, params);
    return formProps.input("RikusouPrice", { label: "陸送料金", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 走行距離フラグ　1:走行距離　2:不明　3:交換　4:改ざん入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 走行距離フラグ　1:走行距離　2:不明　3:交換　4:改ざん入力フィールドのJSX要素
   */
  const input_Soukou_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Soukou_flg form.Soukou_flg:${form?.Soukou_flg || ""} params:`, params);
    return formProps.input("Soukou_flg", { label: "走行距離フラグ　1:走行距離　2:不明　3:交換　4:改ざん", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 走行距離入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 走行距離入力フィールドのJSX要素
   */
  const input_Soukou = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Soukou form.Soukou:${form?.Soukou || ""} params:`, params);
    return formProps.input("Soukou", { label: "走行距離", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：前部入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：前部入力フィールドのJSX要素
   */
  const input_Sonshou01 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sonshou01 form.Sonshou01:${form?.Sonshou01 || ""} params:`, params);
    return formProps.input("Sonshou01", { label: "損傷箇所：前部", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：後部入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：後部入力フィールドのJSX要素
   */
  const input_Sonshou02 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sonshou02 form.Sonshou02:${form?.Sonshou02 || ""} params:`, params);
    return formProps.input("Sonshou02", { label: "損傷箇所：後部", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：右側入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：右側入力フィールドのJSX要素
   */
  const input_Sonshou03 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sonshou03 form.Sonshou03:${form?.Sonshou03 || ""} params:`, params);
    return formProps.input("Sonshou03", { label: "損傷箇所：右側", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：左側入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：左側入力フィールドのJSX要素
   */
  const input_Sonshou04 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sonshou04 form.Sonshou04:${form?.Sonshou04 || ""} params:`, params);
    return formProps.input("Sonshou04", { label: "損傷箇所：左側", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：ルーフ入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：ルーフ入力フィールドのJSX要素
   */
  const input_Sonshou05 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sonshou05 form.Sonshou05:${form?.Sonshou05 || ""} params:`, params);
    return formProps.input("Sonshou05", { label: "損傷箇所：ルーフ", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：盗難入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：盗難入力フィールドのJSX要素
   */
  const input_Sonshou06 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sonshou06 form.Sonshou06:${form?.Sonshou06 || ""} params:`, params);
    return formProps.input("Sonshou06", { label: "損傷箇所：盗難", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：水没入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：水没入力フィールドのJSX要素
   */
  const input_Sonshou07 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sonshou07 form.Sonshou07:${form?.Sonshou07 || ""} params:`, params);
    return formProps.input("Sonshou07", { label: "損傷箇所：水没", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：炎上入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：炎上入力フィールドのJSX要素
   */
  const input_Sonshou08 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sonshou08 form.Sonshou08:${form?.Sonshou08 || ""} params:`, params);
    return formProps.input("Sonshou08", { label: "損傷箇所：炎上", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：エアバッグ展開入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：エアバッグ展開入力フィールドのJSX要素
   */
  const input_Sonshou09 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sonshou09 form.Sonshou09:${form?.Sonshou09 || ""} params:`, params);
    return formProps.input("Sonshou09", { label: "損傷箇所：エアバッグ展開", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：横転入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：横転入力フィールドのJSX要素
   */
  const input_Sonshou10 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sonshou10 form.Sonshou10:${form?.Sonshou10 || ""} params:`, params);
    return formProps.input("Sonshou10", { label: "損傷箇所：横転", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：下回り入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：下回り入力フィールドのJSX要素
   */
  const input_Sonshou11 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sonshou11 form.Sonshou11:${form?.Sonshou11 || ""} params:`, params);
    return formProps.input("Sonshou11", { label: "損傷箇所：下回り", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 損傷箇所：詳細不明入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 損傷箇所：詳細不明入力フィールドのJSX要素
   */
  const input_Sonshou12 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sonshou12 form.Sonshou12:${form?.Sonshou12 || ""} params:`, params);
    return formProps.input("Sonshou12", { label: "損傷箇所：詳細不明", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 足回り　1:不明　2:OK　9:NG入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 足回り　1:不明　2:OK　9:NG入力フィールドのJSX要素
   */
  const input_Ashimawari = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Ashimawari form.Ashimawari:${form?.Ashimawari || ""} params:`, params);
    return formProps.input("Ashimawari", { label: "足回り　1:不明　2:OK　9:NG", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 足回りNG：左前入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 足回りNG：左前入力フィールドのJSX要素
   */
  const input_AshimawariNG1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_AshimawariNG1 form.AshimawariNG1:${form?.AshimawariNG1 || ""} params:`, params);
    return formProps.input("AshimawariNG1", { label: "足回りNG：左前", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 足回りNG：右前入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 足回りNG：右前入力フィールドのJSX要素
   */
  const input_AshimawariNG2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_AshimawariNG2 form.AshimawariNG2:${form?.AshimawariNG2 || ""} params:`, params);
    return formProps.input("AshimawariNG2", { label: "足回りNG：右前", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 足回りNG：左後入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 足回りNG：左後入力フィールドのJSX要素
   */
  const input_AshimawariNG3 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_AshimawariNG3 form.AshimawariNG3:${form?.AshimawariNG3 || ""} params:`, params);
    return formProps.input("AshimawariNG3", { label: "足回りNG：左後", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 足回りNG：右後入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 足回りNG：右後入力フィールドのJSX要素
   */
  const input_AshimawariNG4 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_AshimawariNG4 form.AshimawariNG4:${form?.AshimawariNG4 || ""} params:`, params);
    return formProps.input("AshimawariNG4", { label: "足回りNG：右後", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * ミッション入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} ミッション入力フィールドのJSX要素
   */
  const input_MissionJyotai = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_MissionJyotai form.MissionJyotai:${form?.MissionJyotai || ""} params:`, params);
    return formProps.input("MissionJyotai", { label: "ミッション", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * エンジン入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} エンジン入力フィールドのJSX要素
   */
  const input_EngineJyotai = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_EngineJyotai form.EngineJyotai:${form?.EngineJyotai || ""} params:`, params);
    return formProps.input("EngineJyotai", { label: "エンジン", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 鍵入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 鍵入力フィールドのJSX要素
   */
  const input_KeyJyotai = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_KeyJyotai form.KeyJyotai:${form?.KeyJyotai || ""} params:`, params);
    return formProps.input("KeyJyotai", { label: "鍵", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 販売可能部品入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 販売可能部品入力フィールドのJSX要素
   */
  const input_SaleOKParts = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_SaleOKParts form.SaleOKParts:${form?.SaleOKParts || ""} params:`, params);
    return formProps.input("SaleOKParts", { label: "販売可能部品", maxlength: 300, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 販売済・販売不可部品入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 販売済・販売不可部品入力フィールドのJSX要素
   */
  const input_SaleNGParts = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_SaleNGParts form.SaleNGParts:${form?.SaleNGParts || ""} params:`, params);
    return formProps.input("SaleNGParts", { label: "販売済・販売不可部品", maxlength: 300, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 在庫ステータス 1:管理中 9:処理済入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 在庫ステータス 1:管理中 9:処理済入力フィールドのJSX要素
   */
  const input_ZaikoStatusID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ZaikoStatusID form.ZaikoStatusID:${form?.ZaikoStatusID || ""} params:`, params);
    return formProps.input("ZaikoStatusID", { label: "在庫ステータス 1:管理中 9:処理済", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 閲覧フラグ　1=見積　2=もぎとり入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 閲覧フラグ　1=見積　2=もぎとり入力フィールドのJSX要素
   */
  const input_ViewFlg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ViewFlg form.ViewFlg:${form?.ViewFlg || ""} params:`, params);
    return formProps.input("ViewFlg", { label: "閲覧フラグ　1=見積　2=もぎとり", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * マッチング対象：外装フロント　1=有効　0=無効入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} マッチング対象：外装フロント　1=有効　0=無効入力フィールドのJSX要素
   */
  const input_CBunruiCD100 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CBunruiCD100 form.CBunruiCD100:${form?.CBunruiCD100 || ""} params:`, params);
    return formProps.input("CBunruiCD100", { label: "マッチング対象：外装フロント　1=有効　0=無効", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * マッチング対象：外装右側面　1=有効　0=無効入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} マッチング対象：外装右側面　1=有効　0=無効入力フィールドのJSX要素
   */
  const input_CBunruiCD110 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CBunruiCD110 form.CBunruiCD110:${form?.CBunruiCD110 || ""} params:`, params);
    return formProps.input("CBunruiCD110", { label: "マッチング対象：外装右側面　1=有効　0=無効", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * マッチング対象：外装左側面　1=有効　0=無効入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} マッチング対象：外装左側面　1=有効　0=無効入力フィールドのJSX要素
   */
  const input_CBunruiCD120 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CBunruiCD120 form.CBunruiCD120:${form?.CBunruiCD120 || ""} params:`, params);
    return formProps.input("CBunruiCD120", { label: "マッチング対象：外装左側面　1=有効　0=無効", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * マッチング対象：外装リア　1=有効　0=無効入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} マッチング対象：外装リア　1=有効　0=無効入力フィールドのJSX要素
   */
  const input_CBunruiCD140 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CBunruiCD140 form.CBunruiCD140:${form?.CBunruiCD140 || ""} params:`, params);
    return formProps.input("CBunruiCD140", { label: "マッチング対象：外装リア　1=有効　0=無効", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * マッチング対象：エンジン　1=有効　0=無効入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} マッチング対象：エンジン　1=有効　0=無効入力フィールドのJSX要素
   */
  const input_CBunruiCD2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CBunruiCD2 form.CBunruiCD2:${form?.CBunruiCD2 || ""} params:`, params);
    return formProps.input("CBunruiCD2", { label: "マッチング対象：エンジン　1=有効　0=無効", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * マッチング対象：ミッション　1=有効　0=無効入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} マッチング対象：ミッション　1=有効　0=無効入力フィールドのJSX要素
   */
  const input_CBunruiCD3 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CBunruiCD3 form.CBunruiCD3:${form?.CBunruiCD3 || ""} params:`, params);
    return formProps.input("CBunruiCD3", { label: "マッチング対象：ミッション　1=有効　0=無効", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * マッチング対象：走行　1=有効　0=無効入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} マッチング対象：走行　1=有効　0=無効入力フィールドのJSX要素
   */
  const input_CBunruiCD4 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CBunruiCD4 form.CBunruiCD4:${form?.CBunruiCD4 || ""} params:`, params);
    return formProps.input("CBunruiCD4", { label: "マッチング対象：走行　1=有効　0=無効", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌型式1入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌型式1入力フィールドのJSX要素
   */
  const input_Sharyo_Katashiki1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Katashiki1 form.Sharyo_Katashiki1:${form?.Sharyo_Katashiki1 || ""} params:`, params);
    return formProps.input("Sharyo_Katashiki1", { label: "車輌型式1", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌型式2入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌型式2入力フィールドのJSX要素
   */
  const input_Sharyo_Katashiki2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Katashiki2 form.Sharyo_Katashiki2:${form?.Sharyo_Katashiki2 || ""} params:`, params);
    return formProps.input("Sharyo_Katashiki2", { label: "車輌型式2", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌型式3入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌型式3入力フィールドのJSX要素
   */
  const input_Sharyo_Katashiki3 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Katashiki3 form.Sharyo_Katashiki3:${form?.Sharyo_Katashiki3 || ""} params:`, params);
    return formProps.input("Sharyo_Katashiki3", { label: "車輌型式3", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌型式4入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌型式4入力フィールドのJSX要素
   */
  const input_Sharyo_Katashiki4 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Katashiki4 form.Sharyo_Katashiki4:${form?.Sharyo_Katashiki4 || ""} params:`, params);
    return formProps.input("Sharyo_Katashiki4", { label: "車輌型式4", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌型式5入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌型式5入力フィールドのJSX要素
   */
  const input_Sharyo_Katashiki5 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Katashiki5 form.Sharyo_Katashiki5:${form?.Sharyo_Katashiki5 || ""} params:`, params);
    return formProps.input("Sharyo_Katashiki5", { label: "車輌型式5", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌型式6入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌型式6入力フィールドのJSX要素
   */
  const input_Sharyo_Katashiki6 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Katashiki6 form.Sharyo_Katashiki6:${form?.Sharyo_Katashiki6 || ""} params:`, params);
    return formProps.input("Sharyo_Katashiki6", { label: "車輌型式6", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌型式7入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌型式7入力フィールドのJSX要素
   */
  const input_Sharyo_Katashiki7 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Katashiki7 form.Sharyo_Katashiki7:${form?.Sharyo_Katashiki7 || ""} params:`, params);
    return formProps.input("Sharyo_Katashiki7", { label: "車輌型式7", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌型式8入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌型式8入力フィールドのJSX要素
   */
  const input_Sharyo_Katashiki8 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Katashiki8 form.Sharyo_Katashiki8:${form?.Sharyo_Katashiki8 || ""} params:`, params);
    return formProps.input("Sharyo_Katashiki8", { label: "車輌型式8", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌型式9入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌型式9入力フィールドのJSX要素
   */
  const input_Sharyo_Katashiki9 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Katashiki9 form.Sharyo_Katashiki9:${form?.Sharyo_Katashiki9 || ""} params:`, params);
    return formProps.input("Sharyo_Katashiki9", { label: "車輌型式9", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 車輌型式10入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 車輌型式10入力フィールドのJSX要素
   */
  const input_Sharyo_Katashiki10 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Sharyo_Katashiki10 form.Sharyo_Katashiki10:${form?.Sharyo_Katashiki10 || ""} params:`, params);
    return formProps.input("Sharyo_Katashiki10", { label: "車輌型式10", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 画像メモ入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 画像メモ入力フィールドのJSX要素
   */
  const input_PictMemo = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_PictMemo form.PictMemo:${form?.PictMemo || ""} params:`, params);
    return formProps.input("PictMemo", { label: "画像メモ", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 成功可否　1=成功 2=失敗入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 成功可否　1=成功 2=失敗入力フィールドのJSX要素
   */
  const input_AuthorResult = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_AuthorResult form.AuthorResult:${form?.AuthorResult || ""} params:`, params);
    return formProps.input("AuthorResult", { label: "成功可否　1=成功 2=失敗", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * リモートホスト入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} リモートホスト入力フィールドのJSX要素
   */
  const input_RemoteHost = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_RemoteHost form.RemoteHost:${form?.RemoteHost || ""} params:`, params);
    return formProps.input("RemoteHost", { label: "リモートホスト", maxlength: 50, isIpAddress: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * ユーザー名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} ユーザー名入力フィールドのJSX要素
   */
  const input_UserName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_UserName form.UserName:${form?.UserName || ""} params:`, params);
    return formProps.input("UserName", { label: "ユーザー名", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 所属コード入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 所属コード入力フィールドのJSX要素
   */
  const input_PostCode = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_PostCode form.PostCode:${form?.PostCode || ""} params:`, params);
    return formProps.input("PostCode", { label: "所属コード", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 所属名。ゲストアカウント（CorpID:99999988-000）は会社名入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 所属名。ゲストアカウント（CorpID:99999988-000）は会社名入力フィールドのJSX要素
   */
  const input_PostName = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_PostName form.PostName:${form?.PostName || ""} params:`, params);
    return formProps.input("PostName", { label: "所属名。ゲストアカウント（CorpID:99999988-000）は会社名", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * メール送信フラグ 1:送信可入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} メール送信フラグ 1:送信可入力フィールドのJSX要素
   */
  const input_EmailSend_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_EmailSend_flg form.EmailSend_flg:${form?.EmailSend_flg || ""} params:`, params);
    return formProps.input("EmailSend_flg", { label: "メール送信フラグ 1:送信可", isNumberOnly: true, isMinus: true, isMailAddress: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * メールアドレス2入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} メールアドレス2入力フィールドのJSX要素
   */
  const input_Email2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Email2 form.Email2:${form?.Email2 || ""} params:`, params);
    return formProps.input("Email2", { label: "メールアドレス2", maxlength: 200, isMailAddress: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * メール送信フラグ 1:送信可入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} メール送信フラグ 1:送信可入力フィールドのJSX要素
   */
  const input_EmailSend_flg2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_EmailSend_flg2 form.EmailSend_flg2:${form?.EmailSend_flg2 || ""} params:`, params);
    return formProps.input("EmailSend_flg2", { label: "メール送信フラグ 1:送信可", isNumberOnly: true, isMinus: true, isMailAddress: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 外部公開フラグ 1:公開入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 外部公開フラグ 1:公開入力フィールドのJSX要素
   */
  const input_Open_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Open_flg form.Open_flg:${form?.Open_flg || ""} params:`, params);
    return formProps.input("Open_flg", { label: "外部公開フラグ 1:公開", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 権限フラグ入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 権限フラグ入力フィールドのJSX要素
   */
  const input_Author_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Author_flg form.Author_flg:${form?.Author_flg || ""} params:`, params);
    return formProps.input("Author_flg", { label: "権限フラグ", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * Zwse接続用ID入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} Zwse接続用ID入力フィールドのJSX要素
   */
  const input_ZwseID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ZwseID form.ZwseID:${form?.ZwseID || ""} params:`, params);
    return formProps.input("ZwseID", { label: "Zwse接続用ID", maxlength: 12, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 離島フラグ　1=離島入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 離島フラグ　1=離島入力フィールドのJSX要素
   */
  const input_ritou_flg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ritou_flg form.ritou_flg:${form?.ritou_flg || ""} params:`, params);
    return formProps.input("ritou_flg", { label: "離島フラグ　1=離島", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * URL入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} URL入力フィールドのJSX要素
   */
  const input_URL = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_URL form.URL:${form?.URL || ""} params:`, params);
    return formProps.input("URL", { label: "URL", maxlength: 200, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 営業時間開始入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 営業時間開始入力フィールドのJSX要素
   */
  const input_OpenTime = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_OpenTime form.OpenTime:${form?.OpenTime || ""} params:`, params);
    return formProps.input("OpenTime", { label: "営業時間開始", maxlength: 30, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 営業時間終了入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 営業時間終了入力フィールドのJSX要素
   */
  const input_CloseTime = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_CloseTime form.CloseTime:${form?.CloseTime || ""} params:`, params);
    return formProps.input("CloseTime", { label: "営業時間終了", maxlength: 30, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 販売店情報入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 販売店情報入力フィールドのJSX要素
   */
  const input_Information = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Information form.Information:${form?.Information || ""} params:`, params);
    return formProps.input("Information", { label: "メッセージ", multiline: true, maxRows: 10, rows: 4, maxlength: 500, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 締日1 99:月末締入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 締日1 99:月末締入力フィールドのJSX要素
   */
  const input_Shime1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shime1 form.Shime1:${form?.Shime1 || ""} params:`, params);
    return formProps.input("Shime1", { label: "締日1 99:月末締", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 締日2 99:月末締入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 締日2 99:月末締入力フィールドのJSX要素
   */
  const input_Shime2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shime2 form.Shime2:${form?.Shime2 || ""} params:`, params);
    return formProps.input("Shime2", { label: "締日2 99:月末締", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 締日3 99:月末締　請求相手先支払日に使用入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 締日3 99:月末締　請求相手先支払日に使用入力フィールドのJSX要素
   */
  const input_Shime3 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shime3 form.Shime3:${form?.Shime3 || ""} params:`, params);
    return formProps.input("Shime3", { label: "締日3 99:月末締　請求相手先支払日に使用", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 支払日1 99:月末締入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 支払日1 99:月末締入力フィールドのJSX要素
   */
  const input_Shiharai1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shiharai1 form.Shiharai1:${form?.Shiharai1 || ""} params:`, params);
    return formProps.input("Shiharai1", { label: "支払日1 99:月末締", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 支払月度1 1:当月 2:翌月 3:翌々月入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 支払月度1 1:当月 2:翌月 3:翌々月入力フィールドのJSX要素
   */
  const input_ShiharaiFlg1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ShiharaiFlg1 form.ShiharaiFlg1:${form?.ShiharaiFlg1 || ""} params:`, params);
    return formProps.input("ShiharaiFlg1", { label: "支払月度1 1:当月 2:翌月 3:翌々月", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 支払日2 99:月末締入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 支払日2 99:月末締入力フィールドのJSX要素
   */
  const input_Shiharai2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shiharai2 form.Shiharai2:${form?.Shiharai2 || ""} params:`, params);
    return formProps.input("Shiharai2", { label: "支払日2 99:月末締", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 支払月度2 1:当月 2:翌月 3:翌々月入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 支払月度2 1:当月 2:翌月 3:翌々月入力フィールドのJSX要素
   */
  const input_ShiharaiFlg2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ShiharaiFlg2 form.ShiharaiFlg2:${form?.ShiharaiFlg2 || ""} params:`, params);
    return formProps.input("ShiharaiFlg2", { label: "支払月度2 1:当月 2:翌月 3:翌々月", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 支払日3 99:月末締入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 支払日3 99:月末締入力フィールドのJSX要素
   */
  const input_Shiharai3 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shiharai3 form.Shiharai3:${form?.Shiharai3 || ""} params:`, params);
    return formProps.input("Shiharai3", { label: "支払日3 99:月末締", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 支払月度3 1:当月 2:翌月 3:翌々月入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 支払月度3 1:当月 2:翌月 3:翌々月入力フィールドのJSX要素
   */
  const input_ShiharaiFlg3 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_ShiharaiFlg3 form.ShiharaiFlg3:${form?.ShiharaiFlg3 || ""} params:`, params);
    return formProps.input("ShiharaiFlg3", { label: "支払月度3 1:当月 2:翌月 3:翌々月", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 決算月入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 決算月入力フィールドのJSX要素
   */
  const input_Kessantsuki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Kessantsuki form.Kessantsuki:${form?.Kessantsuki || ""} params:`, params);
    return formProps.input("Kessantsuki", { label: "決算月", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 課税方式 1:明細単位 2:伝票単位 3:請求単位入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 課税方式 1:明細単位 2:伝票単位 3:請求単位入力フィールドのJSX要素
   */
  const input_Kazeihoushiki = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Kazeihoushiki form.Kazeihoushiki:${form?.Kazeihoushiki || ""} params:`, params);
    return formProps.input("Kazeihoushiki", { label: "課税方式 1:明細単位 2:伝票単位 3:請求単位", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 消費税端数　1=四捨五入、2=切り捨て、3=切り上げ入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 消費税端数　1=四捨五入、2=切り捨て、3=切り上げ入力フィールドのJSX要素
   */
  const input_Kazei_kiri = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Kazei_kiri form.Kazei_kiri:${form?.Kazei_kiri || ""} params:`, params);
    return formProps.input("Kazei_kiri", { label: "消費税端数　1=四捨五入、2=切り捨て、3=切り上げ", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 請求先(レコードが支店の場合に有効)　付け替え先CorpIDを入れる入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 請求先(レコードが支店の場合に有効)　付け替え先CorpIDを入れる入力フィールドのJSX要素
   */
  const input_Seikyu_oya = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Seikyu_oya form.Seikyu_oya:${form?.Seikyu_oya || ""} params:`, params);
    return formProps.input("Seikyu_oya", { label: "請求先(レコードが支店の場合に有効)　付け替え先CorpIDを入れる", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 支払元(レコードが支店の場合に有効) 　付け替え先CorpIDを入れる入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 支払元(レコードが支店の場合に有効) 　付け替え先CorpIDを入れる入力フィールドのJSX要素
   */
  const input_Shiharai_oya = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Shiharai_oya form.Shiharai_oya:${form?.Shiharai_oya || ""} params:`, params);
    return formProps.input("Shiharai_oya", { label: "支払元(レコードが支店の場合に有効) 　付け替え先CorpIDを入れる", maxlength: 20, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 0=売掛有効 9=売掛無効入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 0=売掛有効 9=売掛無効入力フィールドのJSX要素
   */
  const input_Corp_Credit = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_Corp_Credit form.Corp_Credit:${form?.Corp_Credit || ""} params:`, params);
    return formProps.input("Corp_Credit", { label: "0=売掛有効 9=売掛無効", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * オークション企業ID入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} オークション企業ID入力フィールドのJSX要素
   */
  const input_AuctionID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_AuctionID form.AuctionID:${form?.AuctionID || ""} params:`, params);
    return formProps.input("AuctionID", { label: "オークション企業ID", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * ログイン後の初期表示ページ　1=部品　2=車輌入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} ログイン後の初期表示ページ　1=部品　2=車輌入力フィールドのJSX要素
   */
  const input_DefaultPage = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_DefaultPage form.DefaultPage:${form?.DefaultPage || ""} params:`, params);
    return formProps.input("DefaultPage", { label: "ログイン後の初期表示ページ　1=部品　2=車輌", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * Movi営業担当者入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} Movi営業担当者入力フィールドのJSX要素
   */
  const input_MoviEigyoTanto = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_MoviEigyoTanto form.MoviEigyoTanto:${form?.MoviEigyoTanto || ""} params:`, params);
    return formProps.input("MoviEigyoTanto", { label: "Movi営業担当者", maxlength: 100, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 生産者フラグ　1=生産者入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 生産者フラグ　1=生産者入力フィールドのJSX要素
   */
  const input_yzFlg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_yzFlg form.yzFlg:${form?.yzFlg || ""} params:`, params);
    return formProps.input("yzFlg", { label: "生産者フラグ　1=生産者", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 生産者企業ID入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 生産者企業ID入力フィールドのJSX要素
   */
  const input_yzCorpID = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_yzCorpID form.yzCorpID:${form?.yzCorpID || ""} params:`, params);
    return formProps.input("yzCorpID", { label: "生産者企業ID", maxlength: 12, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 口座住所1入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 口座住所1入力フィールドのJSX要素
   */
  const input_KouzaAddr1 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_KouzaAddr1 form.KouzaAddr1:${form?.KouzaAddr1 || ""} params:`, params);
    return formProps.input("KouzaAddr1", { label: "口座住所1", maxlength: 80, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 口座住所2入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 口座住所2入力フィールドのJSX要素
   */
  const input_KouzaAddr2 = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_KouzaAddr2 form.KouzaAddr2:${form?.KouzaAddr2 || ""} params:`, params);
    return formProps.input("KouzaAddr2", { label: "口座住所2", maxlength: 160, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 印刷フラグ 1:印刷入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 印刷フラグ 1:印刷入力フィールドのJSX要素
   */
  const input_PrintFlg = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_PrintFlg form.PrintFlg:${form?.PrintFlg || ""} params:`, params);
    return formProps.input("PrintFlg", { label: "印刷フラグ 1:印刷", isNumberOnly: true, isMinus: true, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 品質入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 品質入力フィールドのJSX要素
   */
  const input_PartsClass = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_PartsClass form.PartsClass:${form?.PartsClass || ""} params:`, params);
    return formProps.input("PartsClass", { label: "品質", maxlength: 50, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };
  /**
   * 本文入力フィールドを生成する関数
   * @param {Object} params - 追加のパラメータ
   * @param {boolean} is_debug - デバッグモードの有効/無効
   * @returns {JSX.Element} 本文入力フィールドのJSX要素
   */
  const input_InfoStr = (params = {}, is_debug = false) => {
    if (is_debug) log.debug(`input_InfoStr form.InfoStr:${form?.InfoStr || ""} params:`, params);
    return formProps.input("InfoStr", { label: "本文", maxlength: 2000, is_standard, autoComplete: "on", style: baseStyle, ...params }, is_debug);
  };

  return {
    input_id,
    input_CorpID,
    input_Seikyu_No,
    input_Wellset_flg,
    input_Seikyu_Gaku,
    input_Gokei_Gaku,
    input_Jyuto_Gaku,
    input_Zan_Gaku,
    input_Movi_Gaku,
    input_zei_gaku1,
    input_zei_gaku2,
    input_dl_flg,
    input_dl_userid,
    input_Bikou,
    input_status,
    input_registid,
    input_henkokind,
    input_deleteid,
    input_MitsumoriNo,
    input_SellCorpID,
    input_BuyCorpID,
    input_Shorui_flg,
    input_Kingaku,
    input_Sharyo_Zei_Gaku,
    input_Movi_Zei_Gaku,
    input_Shorui_Gaku,
    input_Shorui_Zei_Gaku,
    input_ShoruiSoryo_Flg,
    input_ShoruiSoufu_Flg,
    input_ShoruiUketori_Chk,
    input_ShoruiUketori_Text,
    input_SiteName,
    input_Seqnum,
    input_CorpName,
    input_Tantosha,
    input_Email,
    input_Tel,
    input_Fax,
    input_ContactKind,
    input_ContactText,
    input_Referer,
    input_UserHostName,
    input_Group_ID,
    input_DenpyouNo,
    input_PAIDs,
    input_DSPID,
    input_BuhSha_flg,
    input_WellsetID,
    input_GroupID,
    input_Expense_amount,
    input_Payment_amount,
    input_SName,
    input_Oya,
    input_Shubetsu,
    input_abMailFlag,
    input_yzMailFlag,
    input_Expense_No,
    input_Expense_kind,
    input_Expense_Finance,
    input_Expense_FIName,
    input_Expense_FIShiten,
    input_Expense_KouzaKind,
    input_Expense_KouzaNo,
    input_Expense_Tegata,

    input_Pay_No,
    input_Pay_kind,
    input_Pay_amount,
    input_Pay_Finance,
    input_Pay_FIName,
    input_Pay_FIShiten,
    input_Pay_KouzaKind,
    input_Pay_KouzaNo,
    input_Pay_Tegata,
    input_Kamoku,
    input_Kamoku_Sonota,
    input_DealPenalty,
    input_ZeiGaku,
    input_Kurikoshi_Gaku,
    input_Suryo,
    input_Soryo_Gaku,
    input_SouGokei_Gaku,
    input_Nebiki_Gaku,
    input_Nebiki_Soryo,
    input_Nebiki_Zei,
    input_Nebiki_SoryoZei,
    input_PRID,
    input_RAID,
    input_PAID,
    input_4CD,
    input_Buy_CorpID,
    input_Sell_CorpID,
    input_SellPrice,
    input_SellSoryo,
    input_ViewPrice,
    input_ViewSoryo,
    input_DealPrice,
    input_DealCarry,
    input_DealPriceSK,
    input_DealCarrySK,
    input_SoryoZeiGaku,
    input_ViewZei,
    input_ViewSoryoZei,
    input_MoviGaku,
    input_Buysell_flg,
    input_Tesuryo_flg,
    input_SBSK_flg,
    input_TokenStr,
    input_UserID,
    input_Pswd,
    input_AreaName,
    input_KeyWord,
    input_GroupName,
    input_Oya_ID,
    input_ZipCode1,
    input_ZipCode2,
    input_Todofuken,
    input_Jyusho1,
    input_Jyusho2,
    input_Jyusho3,
    input_Fax2,
    input_FIName,
    input_FIShiten,
    input_KouzaKind,
    input_KouzaNo,
    input_Meiginin,
    input_Tesuryo,
    input_CashBack,
    input_CashBackRatio,
    input_Shimebi,
    input_Shiten,
    input_Shiharaibi,
    input_LogoFileName,
    input_AgreementFileName,
    input_ManualDLFileName,
    input_AgreementDLFileName,
    input_UpdateFileName,
    input_Keisansho_flg,
    input_CarView_flg,
    input_NentrysFlg,
    input_CorpKind,
    input_GyoshuName,
    input_C_Shorui_flg,
    input_C_HikitoriKanoubi,
    input_C_NyusatsuKingaku,
    input_Hacchu_flg,
    input_C_Recycle_flg,
    input_C_Recycle_price,
    input_C_ShakenZan,
    input_C_HokanbashoT,
    input_C_HokanbashoP,
    input_C_HokanbashoS,
    input_C_HokanShozaiName,
    input_C_HokanShozaiTanto,
    input_C_HokanShozaiTel,
    input_C_HokanShozaiBikou,
    input_C_Shareki,
    input_C_Soukou,
    input_C_Nenshiki,
    input_C_Shufuku,
    input_C_TourokuChimei,
    input_C_Touroku,
    input_C_Shoyusha,
    input_C_Sonshou01,
    input_C_Sonshou02,
    input_C_Sonshou03,
    input_C_Sonshou04,
    input_C_Sonshou05,
    input_C_Sonshou06,
    input_C_Sonshou07,
    input_C_Sonshou08,
    input_C_Sonshou09,
    input_C_Sonshou10,
    input_C_Ashimawari,
    input_C_AshimawariNG1,
    input_C_AshimawariNG2,
    input_C_AshimawariNG3,
    input_C_AshimawariNG4,
    input_C_EngineJyotai,
    input_C_KeyJyotai,
    input_C_Hikitorihouhou,
    input_C_SharyoKanban,
    input_C_KibouKingaku,
    input_Memo,
    input_Tanto,
    input_Claim,
    input_JishaNo,
    input_PartsJyunseiCode,
    input_PartsKindID,
    input_Eval_Kakaku,
    input_Eval_Hinshitsu,
    input_Eval_Konpou,
    input_Eval_Nouki,
    input_Eval_Sougou,
    input_DocPath,
    input_HaisoPAID,
    input_PartsName,
    input_Message,
    input_PrepKind,
    input_OthParts4CD,
    input_OthPartsName,
    input_OthPartsSonota,
    input_StanderdPrice,
    input_Soryo,
    input_Hasso,
    input_ColorNo,
    input_ColorName,
    input_TrimNo,
    input_Haiso_kubun,
    input_Haiso_Corp,
    input_Haiso_denpyo,
    input_HassouBikou,
    input_Chumonsha,
    input_del_flg,
    input_DairiTouroku,
    input_DPR_ID,
    input_JyunseiCode,
    input_JyunseiCode_M,
    input_Kakaku,
    input_Suuryo,
    input_MakerName,
    input_St,
    input_Ed,
    input_Tekiyo,
    input_Zwse4CD,
    input_P_PartsName,
    input_P_PKID1,
    input_P_PKID2,
    input_P_PKID3,
    input_P_PKID4,
    input_P_PKID5,
    input_PictPath,
    input_Q_no,
    input_QA_flg,
    input_QAnaiyo,
    input_al_flg,
    input_PCA_ID,
    input_Oya_MitsumoriNo,
    input_GenshaID,
    input_al_flg2,
    input_view_flg,
    input_Kaitosha,
    input_BikouYZ,
    input_AreaCode,
    input_Katashiki,
    input_Ruibetsu,
    input_ShadaiKatashiki,
    input_Shadaibango,
    input_MakerCD,
    input_CarName,
    input_KanrenKatashiki,
    input_Sharyo_Katashiki,
    input_Sharyo_Nenshiki,
    input_Sharyo_Grade,
    input_Sharyo_EngineKatashiki,
    input_Sharyo_HaigasKisei,
    input_Sharyo_Haikiryo,
    input_Sharyo_Ekubun,
    input_Sharyo_TransMission,
    input_Sharyo_KudouHoushiki,
    input_Sharyo_ColorNo,
    input_Sharyo_Color,
    input_Sharyo_TrimNo,
    input_Sharyo_Trim,
    input_Sharyo_Handle,
    input_Sharyo_Remark_Nm,
    input_Sharyo_Body_Nm,
    input_Sharyo_Sya_Type,
    input_I_User,
    input_I_UserTEL,
    input_I_Corp,
    input_I_InsuranceCorp,
    input_I_InsuranceSC,
    input_I_InsuranceTanto,
    input_Shonendo,
    input_ShakenManryo,
    input_TorokuBango1,
    input_TorokuBango2,
    input_TorokuBango3,
    input_TorokuBango4,
    input_Oth_SysID,
    input_Oth_SysNo,
    input_Hassosaki_Corp,
    input_Hassosaki_Tanto,
    input_Hassosaki_ZipCode1,
    input_Hassosaki_ZipCode2,
    input_Hassosaki_Todofuken,
    input_Hassosaki_Address,
    input_Hassosaki_Tel1,
    input_Hassosaki_Tel2,
    input_Hassosaki_Tel3,
    input_Hassosaki_Fax,
    input_Sai_MitsumoriNo,
    input_Sai_Reason,
    input_SonotaText,
    input_Sai_MitsumoriFlg,
    input_Taisho_ShadaiBango,
    input_Taisho_MakerCD,
    input_Taisho_CarName,
    input_Taisho_Katashiki,
    input_Taisho_Ruibetsu,
    input_Taisho_SharyoKatashiki,
    input_Input_Status,
    input_AuctionNo,
    input_DummyKingaku,
    input_AuctionIraisha,
    input_DIC_ShokikanNo_F,
    input_DIC_ShokikanNo_S,
    input_DIC_ShokikanNo_T,
    input_Jyusho,
    input_CarKindID,
    input_CarKindName,
    input_PartsKindName,
    input_KanriID,
    input_FAX,
    input_CloseFlg,
    input_CloseDate,
    input_HolidayFlg,
    input_CloseMemo,
    input_Title,
    input_Naiyo,
    input_Gazo,
    input_RegKind,
    input_ID,
    input_ColorCD,
    input_Color2Tone,
    input_Hoshuu,
    input_CarCD,
    input_CarSubCD,
    input_SharyoKatashiki,
    input_SharyoFullKatashiki,
    input_ShadaiBango_From,
    input_ShadaiBango_To,
    input_Sharyo_Shubetsu,
    input_Shatai_Keijyo,
    input_Sharyo_Keijyo,
    input_Sharyo_Youto,
    input_Sharyo_EngineKatashiki2,
    input_Sharyo_Nenshiki_st,
    input_Sharyo_Nenshiki_ed,
    input_Sharyo_Kibetsu,
    input_Shiyo_Gaiso,
    input_Shiyo_Engine,
    input_Shiyo_Soukou,
    input_Shiyo_TransNo,
    input_Shiyo_Tokushu,
    input_Shiyo_EngineNo,
    input_Shiyo_GearRatio,
    input_Jyosha1,
    input_Jyosha2,
    input_Sekisai1,
    input_Sekisai2,
    input_Jyuryo1,
    input_Jyuryo2,
    input_Sojyuryo1,
    input_Sojyuryo2,
    input_Length,
    input_Width,
    input_Height,
    input_MMJiku,
    input_MGJiku,
    input_GMJiku,
    input_GGJiku,
    input_F_tiresize,
    input_R_tiresize,
    input_F_Wheelsize,
    input_F_Wheeloffset,
    input_R_Wheelsize,
    input_R_Wheeloffset,
    input_BatterySize,
    input_Steering,
    input_District,
    input_Memo_Bikou,
    input_Freon,
    input_AirBag,
    input_Zwes_Flag,
    input_BunruiCD,
    input_BunruiCD_oya,
    input_Base4CD,
    input_BunruiName,
    input_Email_Keiri,
    input_CorpKana,
    input_DaihyoName1,
    input_DaihyoName2,
    input_DaihyoKana1,
    input_DaihyoKana2,
    input_JyuchuNoFormat,
    input_JyuchuNoKuwake,
    input_KarteNoFormat,
    input_KarteNoKuwake,
    input_DelivKind,
    input_Search_flg,
    input_Factoring,
    input_Hoshokin,
    input_Gyoshu_flg,
    input_BSCode,
    input_Sedina,
    input_Yayoi_UriNo,
    input_Yayoi_KaiNo,
    input_Oya_CorpID,
    input_Sname,
    input_seq,
    input_PNPid,
    input_Chimei,
    input_Todo,
    input_Kuiki,
    input_Hyouji,
    input_Zwse4CD2,
    input_Zwse4CD3,
    input_Zwse4CD4,
    input_Zwse4CD5,
    input_AyFlg,
    input_co2_flg,
    input_co2_num,
    input_co2_flg_r,
    input_co2_num_r,
    input_Zeiritsu,
    input_Shikuchoson,
    input_Chouiki,
    input_ShadaiBango,
    input_MissionKatashiki,
    input_MTNum,
    input_Bariki,
    input_BodyKeijyo,
    input_CabinKeijyo,
    input_Denatsu,
    input_HokanBasho,
    input_PurchaTanto,
    input_PurchaItem,
    input_PurchaCorp,
    input_PurchaPrice,
    input_RikusouTanto,
    input_RikusouPrice,
    input_Soukou_flg,
    input_Soukou,
    input_Sonshou01,
    input_Sonshou02,
    input_Sonshou03,
    input_Sonshou04,
    input_Sonshou05,
    input_Sonshou06,
    input_Sonshou07,
    input_Sonshou08,
    input_Sonshou09,
    input_Sonshou10,
    input_Sonshou11,
    input_Sonshou12,
    input_Ashimawari,
    input_AshimawariNG1,
    input_AshimawariNG2,
    input_AshimawariNG3,
    input_AshimawariNG4,
    input_MissionJyotai,
    input_EngineJyotai,
    input_KeyJyotai,
    input_SaleOKParts,
    input_SaleNGParts,
    input_ZaikoStatusID,
    input_ViewFlg,
    input_CBunruiCD100,
    input_CBunruiCD110,
    input_CBunruiCD120,
    input_CBunruiCD140,
    input_CBunruiCD2,
    input_CBunruiCD3,
    input_CBunruiCD4,
    input_Sharyo_Katashiki1,
    input_Sharyo_Katashiki2,
    input_Sharyo_Katashiki3,
    input_Sharyo_Katashiki4,
    input_Sharyo_Katashiki5,
    input_Sharyo_Katashiki6,
    input_Sharyo_Katashiki7,
    input_Sharyo_Katashiki8,
    input_Sharyo_Katashiki9,
    input_Sharyo_Katashiki10,
    input_PictMemo,
    input_AuthorResult,
    input_RemoteHost,
    input_UserName,
    input_PostCode,
    input_PostName,
    input_EmailSend_flg,
    input_Email2,
    input_EmailSend_flg2,
    input_Open_flg,
    input_Author_flg,
    input_ZwseID,
    input_ritou_flg,
    input_URL,
    input_OpenTime,
    input_CloseTime,
    input_Information,
    input_Shime1,
    input_Shime2,
    input_Shime3,
    input_Shiharai1,
    input_ShiharaiFlg1,
    input_Shiharai2,
    input_ShiharaiFlg2,
    input_Shiharai3,
    input_ShiharaiFlg3,
    input_Kessantsuki,
    input_Kazeihoushiki,
    input_Kazei_kiri,
    input_Seikyu_oya,
    input_Shiharai_oya,
    input_Corp_Credit,
    input_AuctionID,
    input_DefaultPage,
    input_MoviEigyoTanto,
    input_yzFlg,
    input_yzCorpID,
    input_KouzaAddr1,
    input_KouzaAddr2,
    input_PrintFlg,
    input_PartsClass,
    input_InfoStr,
  };
};

export default useInputEx;
